/* eslint-disable unicode-bom */
import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { Attribute } from '../../types'; // Assuming Attribute type is defined here
import Tooltip from './Tooltip'; // Assuming Tooltip component is available
import './ControlRender.firefox.css';
import { ENTITY_NAMES, FIELD_PATTERNS, AID_CALCULATION_OPTIONS, FIELD_NAMES as COMMON_FIELD_NAMES, BOOLEAN_VALUES, LOCATION_VALUES } from '../../constants';
import { IMaskInput } from 'react-imask';
import { MASKED_FIELD_NAMES, FIELD_NAMES, MASK_PATTERNS } from '../../shared/constants/maskFields';
import { useTenant } from '../TenantContext';
import { useHighSchoolSelection } from '../../hooks/useHighSchoolSelection';
import type { HighSchoolData } from '../../utils/storage';
import { getHighSchoolData } from '../../utils/storage';
import { booleanValueToDisplay } from '../../utils/booleanTransformers';
import DatePicker from '../ui/DatePicker';
import { validateNumericAttribute } from '../../utils/validationUtils';
import DynamicDescriptionRenderer from './DynamicDescriptionRenderer';
import { extractAndStoreNeedBasedGrant } from '../../utils/needBasedGrantConditional';
import { determinePreheaderText, determineFooterText } from '../../utils/summaryHeaderFooterLogic';
import { normalizeName, isFinancialControl } from '../../utils/currencyDetection';
import { shouldShowConditionalField } from '../../utils/academicScoresFilter';

// ChevronDownIcon component for accordion arrows
const ChevronDownIcon: React.FC<{ className?: string }> = ({ className = '' }) => (
    <svg
        className={className}
        viewBox="0 0 20 20"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden="true"
    >
        <path
            d="M5 7L10 12L15 7"
            stroke="currentColor"
            strokeWidth="1.75"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
    </svg>
);

// Component to render text with clickable links
const TextWithLinks: React.FC<{ text: string; className?: string }> = ({ text, className }) => {
    if (!text) return null;

    // First, decode HTML entities
    let decodedText = text
        .replace(/&lt;/g, '<')
        .replace(/&gt;/g, '>')
        .replace(/&quot;/g, '"')
        .replace(/&#39;/g, "'")
        .replace(/&amp;/g, '&');

    // Intelligently replace anchor tag text: only show "here" if preceded by "Click", otherwise show full link
    // Safe regex: use \b (word boundary) instead of \s+ to avoid overlapping quantifiers on [^>]* (ReDoS fix)
    decodedText = decodedText.replace(/<a\b([^>]*)>([^<]+)<\/a>/g, (match, attrs, linkText) => {
        // Get the text before the link to check if "Click" appears
        const textBeforeLink = decodedText.substring(0, decodedText.indexOf(match));
        const wordsBeforeLink = textBeforeLink.toLowerCase().trim().split(/\s+/).slice(-2); // Get last 2 words before link

        // Check if "click" appears in the words before the link
        const hasClickWord = wordsBeforeLink.some(word => word.replace(/[.,;:!?]/g, '') === 'click');

        return hasClickWord
            ? `<a ${attrs} style="color: #2563eb; text-decoration: underline;">this link</a>`
            : `<a ${attrs} style="color: #2563eb; text-decoration: underline;">${linkText}</a>`;
    });

    // Then, handle escaped newlines (literal \n strings) by converting them to actual newlines
    const normalizedText = decodedText.replace(/\\n/g, '\n');

    // Check if there are HTML tags (anchor, bold, italic, strong, em, br) - if so, render with dangerouslySetInnerHTML
    // Safe regex: use \b instead of \s+ before [^>]* to avoid overlapping quantifiers (ReDoS fix)
    if (/<a\b[^>]*>|<\/?(b|i|strong|em)>|<br\s*\/?>/.test(normalizedText)) {
        // Replace newlines with <br /> for HTML rendering
        // Also remove asterisks around HTML tags
        // Also convert raw URLs to clickable links (not already inside href/src attributes)
        const htmlContent = normalizedText
            .replace(/\n/g, '<br />')
            .replace(/\*(<\/?(b|i|strong|em)>)\*/g, '$1')
            .replace(/(?<![="'])(https?:\/\/[^\s<>"')\]]+)/g, (url) =>
                `<a href="${url}" target="_blank" rel="noopener noreferrer" style="color: #2563eb; text-decoration: underline;">${url}</a>`);
        return <span className={className} dangerouslySetInnerHTML={{ __html: htmlContent }} />;
    }

    // Otherwise, use the original logic for plain URLs
    // Regex to identify URLs starting with http, https, or www.
    const urlRegex = /(https?:\/\/[^\s]+|www\.[^\s]+)/g;

    // Split the text into an array of strings and URL matches
    const parts = normalizedText.split(urlRegex);

    return (
        <span className={className}>
            {parts.map((part, index) => {
                // Check if the current part matches the URL pattern
                if (urlRegex.exec(part)) {
                    const href = part.startsWith('www.') ? `https://${part}` : part;
                    return (
                        <a
                            key={`url-${href}-${index}`}
                            href={href}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 underline hover:text-blue-800"
                            style={{ cursor: 'pointer' }}
                            onClick={(e) => e.stopPropagation()}
                        >
                            {part}
                        </a>
                    );
                }
                // Return plain text for non-URL parts, handling \n as line breaks
                return (
                    <React.Fragment key={`text-${part.slice(0, 20)}`}>
                        {part.split('\n').map((line, lineIndex, array) => (
                            <React.Fragment key={line || `br-${part.slice(0, 10)}-${lineIndex}`}>
                                {line}
                                {lineIndex < array.length - 1 && <br />}
                            </React.Fragment>
                        ))}
                    </React.Fragment>
                );
            })}
        </span>
    );
};

// Helper: get replacement rule based on control and entity context
function getReplacementForControlName(controlName?: string, entityName?: string): { from: string; to: string } | undefined {
    if (!controlName) return undefined;
}

// Helper: compare numeric portion of currency-like strings (e.g., "$2,000,000" vs "$2,000,000.00")
function numericDigitsOf(s?: string): string {
    if (!s) return '';
    return String(s).replace(/\D/g, '');
}

// Helper: apply a from?to replacement to a single string field
function applyStringReplacement(text: string, from: string, to: string): { value: string; changed: boolean } {
    const trimmed = text.trim();
    if (trimmed.includes('+')) return { value: text, changed: false };
    const digitsText = numericDigitsOf(trimmed);
    const digitsFrom = numericDigitsOf(from);
    if (digitsText && digitsFrom && digitsText === digitsFrom) return { value: to, changed: true };
    if (trimmed === from) return { value: to, changed: true };
    if (trimmed.includes(from)) return { value: trimmed.replace(from, to), changed: true };
    return { value: text, changed: false };
}

// Helper: adjust option item (string or object)
function adjustOptionItemForControl(
    item: string | { label?: string; value?: string;[key: string]: any },
    controlName?: string,
    entityName?: string
): string | { label?: string; value?: string;[key: string]: any } {
    const replacement = getReplacementForControlName(controlName, entityName);
    if (!replacement) return item;

    const { from, to } = replacement;

    // Handle string options
    if (typeof item === 'string') {
        return applyStringReplacement(item, from, to).value;
    }

    // Handle object options
    if (typeof item === 'object' && item !== null) {
        const copy = { ...item };
        let changed = false;

        if (typeof copy.label === 'string') {
            const result = applyStringReplacement(copy.label, from, to);
            copy.label = result.value;
            changed = changed || result.changed;
        }

        if (typeof copy.value === 'string') {
            const result = applyStringReplacement(copy.value, from, to);
            copy.value = result.value;
            changed = changed || result.changed;
        }

        return changed ? copy : item;
    }

    return item;
}

// Helper: apply replacement to all options in array
function applyControlMaxReplacement(
    finalOptions: any[],
    controlName?: string,
    entityName?: string
): any[] {
    if (!controlName || !Array.isArray(finalOptions) || finalOptions.length === 0) {
        return finalOptions;
    }

    const replacement = getReplacementForControlName(controlName, entityName);
    if (!replacement) return finalOptions;

    // Adjust ALL options, not just the last one
    // This ensures all matching options get the '+' suffix
    const adjustedOptions = finalOptions.map(item =>
        adjustOptionItemForControl(item, controlName, entityName)
    );

    return adjustedOptions;
}

// Helper: check if possibleValues contains non-numeric string options
function hasStringOptions(possibleValues: string | null | undefined): boolean {
    if (!possibleValues || typeof possibleValues !== 'string') {
        return false;
    }

    const options = possibleValues.split(',').map(opt => opt.trim()).filter(opt => opt.length > 0);

    // Check if any option is non-numeric (excluding spaces, commas, and + suffix)
    return options.some(option => {
        const cleanOption = option.replaceAll(/[,+\s]/g, '');
        return Number.isNaN(Number(cleanOption)) || cleanOption === '';
    });
}

// Export helpers for unit testing and local verification
export {
    // CONTROL_MAX_REPLACEMENTS,
    normalizeName,
    numericDigitsOf,
    getReplacementForControlName,
    adjustOptionItemForControl,
    applyControlMaxReplacement,
    isFinancialControl,
    hasStringOptions,
};

interface Control {
    _id: string;
    name: string;
    displayName?: string;
    [key: string]: any;
}

// Shared helper: renders a control description with line-break support
const DescriptionText: React.FC<{ text?: string | null }> = ({ text }) => {
    if (!text) return null;
    return (
        <p className="text-sm text-gray-600 mt-1">
            {text.split('\n').map((line, index, array) => (
                <React.Fragment key={line || `br-${index}`}>
                    {line}
                    {index < array.length - 1 && <br />}
                </React.Fragment>
            ))}
        </p>
    );
};

// Helper function to check if user should skip to Summary (same as in DynamicTab.tsx)
function shouldSkipToSummary(familyAttributes: Control[], formValues: Record<string, string>): boolean {
    // First check for aid calculation method
    const aidCalculationAttribute = familyAttributes.find(attr =>
        attr.name.toLowerCase().includes('aid calculation') ||
        attr.name.toLowerCase().includes('calculation method')
    );

    if (aidCalculationAttribute) {
        const selectedOption = formValues[aidCalculationAttribute._id];

        // If "Help me calculate" selected, DON'T skip (so return false)
        if (selectedOption === AID_CALCULATION_OPTIONS.NEED_BASED) {
            return false;
        }

        // If SAI option selected and SAI value provided, skip to Summary
        if (selectedOption === AID_CALCULATION_OPTIONS.SAI) {
            const saiAttribute = familyAttributes.find(attr =>
                attr.name === 'Student Aid Index' ||
                attr.name.toLowerCase().includes('sai') ||
                attr.name.toLowerCase().includes('student aid')
            );
            return !!(saiAttribute && formValues[saiAttribute._id]?.trim());
        }

        // If skip options selected, skip to Summary
        if (selectedOption === AID_CALCULATION_OPTIONS.SKIP_INTERNATIONAL ||
            selectedOption === AID_CALCULATION_OPTIONS.SKIP_NO_NEED) {
            return true;
        }
    }

    // Fallback: check for SAI without aid calculation method (legacy behavior)
    const saiAttribute = familyAttributes.find(attr =>
        attr.name === 'Student Aid Index' ||
        attr.name.toLowerCase().includes('sai') ||
        attr.name.toLowerCase().includes('student aid')
    );
    if (saiAttribute && formValues[saiAttribute._id]?.trim()) {
        return true;
    }

    // Fallback: check for skip financial aid options
    const skipFinancialAidAttribute = familyAttributes.find(attr =>
        FIELD_PATTERNS.SKIP_FINANCIAL_AID.some(pattern =>
            attr.name.toLowerCase().includes(pattern)
        )
    );
    if (skipFinancialAidAttribute) {
        const value = formValues[skipFinancialAidAttribute._id];
        if (skipFinancialAidAttribute.dataType === 'Boolean') {
            return value === 'true';
        } else {
            return !!value?.trim();
        }
    }

    return false;
}

// Extracted HighSchoolAutocomplete for cognitive complexity
const HighSchoolAutocomplete: React.FC<{
    controlKey: string;
    labelText: string;
    ctrl: Attribute;
    errorMessage: string;
    currentControlValue: string;
    dynamicOptions: string[] | null;
    effectiveDynamicOptions?: any[] | null;
    isLoading: boolean;
    onChange: (_id: string, value: any) => void;
    disabled?: boolean;
    isHomeschooled?: boolean;
}> = (props) => {
    const {
        controlKey,
        labelText,
        ctrl,
        errorMessage,
        currentControlValue,
        dynamicOptions,
        effectiveDynamicOptions,
        isLoading: isLoadingOptions,
        onChange,
        disabled,
        isHomeschooled = false
    } = props;

    const [searchText, setSearchText] = useState('');
    const [searchResults, setSearchResults] = useState<HighSchoolData[]>([]);
    const [isSearching, setIsSearching] = useState(false);
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const inputRef = useRef<HTMLInputElement>(null);
    const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const hasInitialized = useRef(false);

    // Memoize the selection change handler to prevent hook re-initialization
    const handleSelectionChange = useCallback((data: HighSchoolData | null) => {
        if (data) {
            // Update form with CEEB code only when user selects
            onChange(controlKey, String(data.ceeb));
        } else {
            // Clear form value when selection is cleared
            onChange(controlKey, '');
        }
    }, [onChange, controlKey]);

    // Use high school selection hook - will auto-load if currentControlValue exists (navigating back)
    const {
        selectedSchool,
        displayName,
        selectSchool,
        clearSelection,
        searchSchoolsByText,
        hasUserSelected,
    } = useHighSchoolSelection({
        initialCeeb: currentControlValue || null,
        autoLoadFromStorage: false,
        onSelectionChange: handleSelectionChange,
    });

    // Only update search text if user has made a selection
    useEffect(() => {
        if (hasUserSelected && displayName && !searchText) {
            setSearchText(displayName);
        }
    }, [displayName, hasUserSelected, searchText]);

    // Initialize field state - blank on initial load, restore on navigation back
    useEffect(() => {
        if (!hasInitialized.current) {
            hasInitialized.current = true;

            // If we have a selected school from localStorage (navigating back), show it
            if (selectedSchool && hasUserSelected) {
                setSearchText(selectedSchool.hsName);
            } else {
                setSearchText('');
            }
        }
    }, [selectedSchool, hasUserSelected]);

    // Clear field when homeschooled is checked
    useEffect(() => {
        if (isHomeschooled) {
            setSearchText('');
            if (hasUserSelected) {
                clearSelection();
            }
        }
    }, [isHomeschooled, hasUserSelected, clearSelection]);

    // Handle search with debouncing
    const handleSearch = useCallback(async (text: string) => {
        if (text.length < 2) {
            setSearchResults([]);
            return;
        }

        setIsSearching(true);
        try {
            const results = await searchSchoolsByText(text);
            setSearchResults(results);
        } catch (error) {
            console.error('Error searching schools:', error);
            setSearchResults([]);
        } finally {
            setIsSearching(false);
        }
    }, [searchSchoolsByText]);

    const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setSearchText(value);

        // Clear selection if user clears the input
        if (!value && hasUserSelected) {
            clearSelection();
        }

        setIsDropdownOpen(value.length >= 2);

        // Debounce search
        if (searchTimeoutRef.current) {
            clearTimeout(searchTimeoutRef.current);
        }

        searchTimeoutRef.current = setTimeout(() => {
            handleSearch(value);
        }, 300);
    }, [handleSearch, setIsDropdownOpen, clearSelection, hasUserSelected]);

    const handleOptionSelect = useCallback((option: HighSchoolData) => {
        // Store in localStorage and update form - marks as user selected
        selectSchool(option.ceeb, option.hsName);

        // Update display
        setSearchText(option.hsName);
        setIsDropdownOpen(false);
        setSearchResults([]);

        // Return focus to input
        inputRef.current?.focus();
    }, [selectSchool, setIsDropdownOpen]);

    const handleInputFocus = useCallback(() => {
        if (searchText.length >= 2) {
            setIsDropdownOpen(true);
        }
    }, [searchText, setIsDropdownOpen]);

    const handleInputBlur = useCallback(() => {
        // Delay to allow option click to register
        setTimeout(() => {
            setIsDropdownOpen(false);
            // Only restore display name if user has made a selection
            if (hasUserSelected && selectedSchool && searchText !== selectedSchool.hsName) {
                setSearchText(selectedSchool.hsName);
            } else if (!hasUserSelected && searchText) {
                // Clear if no selection made
                setSearchText('');
            }
        }, 200);
    }, [setIsDropdownOpen, selectedSchool, searchText, hasUserSelected]);

    const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Escape') {
            setIsDropdownOpen(false);
            // Restore display name only if user has selected
            if (hasUserSelected && selectedSchool) {
                setSearchText(selectedSchool.hsName);
            } else {
                setSearchText('');
            }
            inputRef.current?.blur();
        }
    }, [setIsDropdownOpen, selectedSchool, hasUserSelected]);

    // Merge search results with dynamic options
    const filteredOptions = useMemo(() => {
        // Prioritize API search results
        if (searchResults.length > 0) {
            return searchResults;
        }

        // Fallback to dynamic options if available
        if (!searchText || searchText.length < 2) return [];

        const options = effectiveDynamicOptions || dynamicOptions || [];
        return options
            .filter((option: any) => {
                const label = typeof option === 'string'
                    ? option
                    : option.label ?? option.hsName ?? '';
                return label.toLowerCase().includes(searchText.toLowerCase());
            })
            .map((option: any) => {
                if (typeof option === 'object' && option.ceeb && option.hsName) {
                    return option;
                }
                // Try to parse string format "Name (CEEB: 12345)"
                if (typeof option === 'string') {
                    // Safe regex: removed \s* before \( to eliminate overlap with .*? (ReDoS fix); match[1].trim() handles trailing spaces
                    const match = /^(.*?)\(CEEB:\s*(\d+)\)/.exec(option);
                    if (match) {
                        return {
                            hsName: match[1].trim(),
                            ceeb: Number.parseInt(match[2], 10)
                        };
                    }
                }
                return null;
            })
            .filter((opt): opt is HighSchoolData => opt !== null);
    }, [searchResults, searchText, effectiveDynamicOptions, dynamicOptions]);

    const isLoading = isLoadingOptions || isSearching;

    return (
        <div className="mb-4">
            <label
                htmlFor={controlKey}
                className="block mb-1 font-medium text-gray-700"
            >
                <div className="flex items-center justify-between w-full">
                    <span>{labelText}{(ctrl.isRequired ?? false) ? ' *' : ''}</span>
                    {ctrl.toolTip && <Tooltip message={ctrl.toolTip}><span>?</span></Tooltip>}
                </div>
            </label>

            <div className="relative">
                <input
                    ref={inputRef}
                    type="text"
                    id={controlKey}
                    name={ctrl.name}
                    value={isHomeschooled ? '' : searchText}
                    onChange={handleInputChange}
                    onFocus={handleInputFocus}
                    onBlur={handleInputBlur}
                    onKeyDown={handleKeyDown}
                    placeholder="Start typing to find your school..."
                    disabled={disabled}
                    className={`w-full p-2 border rounded-md ${errorMessage && !disabled ? 'border-red-500' : 'border border-gray-600'
                        } ${disabled ? 'bg-gray-100' : ''}`}
                    autoComplete="off"
                    role="combobox"
                    aria-autocomplete="list"
                    aria-controls={`${controlKey}-listbox`}
                    aria-expanded={isDropdownOpen}
                    aria-describedby={errorMessage ? `${controlKey}-error` : undefined}
                />

                {isLoading && (
                    <div
                        className="absolute right-3 top-1/2 -translate-y-1/2"
                        aria-label="Loading"
                    >
                        <div className="animate-spin h-4 w-4 border-2 border-gray-300 border-t-blue-600 rounded-full" />
                    </div>
                )}

                {isDropdownOpen && filteredOptions.length > 0 && (
                    <div
                        id={`${controlKey}-listbox`}
                        role="listbox"
                        className="absolute z-50 w-full mt-1 max-h-60 overflow-auto bg-white border border-gray-600 rounded-md shadow-lg"
                    >
                        {filteredOptions.map((option: HighSchoolData, index: number) => {
                            const isSelected = selectedSchool?.ceeb === option.ceeb;

                            return (
                                <div
                                    key={`${option.ceeb}-${index}`}
                                    role="option"
                                    aria-selected={isSelected}
                                    className={`px-3 py-2 cursor-pointer hover:bg-blue-50 focus:bg-blue-50 focus:outline-none ${isSelected ? 'bg-blue-100' : ''
                                        }`}
                                    onMouseDown={() => handleOptionSelect(option)}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter' || e.key === ' ') {
                                            e.preventDefault();
                                            handleOptionSelect(option);
                                        }
                                    }}
                                    tabIndex={0}
                                >
                                    <div className="flex flex-col">
                                        <span className="font-medium text-gray-900">
                                            {option.hsName}
                                        </span>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}

                {isDropdownOpen &&
                    searchText.length >= 2 &&
                    filteredOptions.length === 0 &&
                    !isLoading && (
                        <div className="absolute z-50 w-full mt-1 p-3 bg-white border border-gray-600 rounded-md shadow-lg">
                            <p className="text-gray-500 text-sm">
                                {searchText}
                            </p>
                        </div>
                    )}
            </div>

            {errorMessage && !disabled && (
                <p
                    id={`${controlKey}-error`}
                    className="text-red-500 text-sm mt-1"
                    role="alert"
                >
                    This field is required
                </p>
            )}

            <DescriptionText text={ctrl.description} />

            {/* Hidden inputs to store data for form submission - only when user has selected */}
            {hasUserSelected && selectedSchool && (
                <>
                    <input
                        type="hidden"
                        name={`${ctrl.name}_ceeb`}
                        value={selectedSchool.ceeb}
                    />
                    <input
                        type="hidden"
                        name={`${ctrl.name}_name`}
                        value={selectedSchool.hsName}
                    />
                </>
            )}
        </div>
    );
};

// Memoize component to prevent unnecessary re-renders that cause focus loss
const HighSchoolAutocompleteComponent = React.memo(HighSchoolAutocomplete, (prevProps, nextProps) => {
    // Only check props that would require a re-render
    // Ignore onChange, ctrl, and other objects that may be recreated but are functionally the same
    return (
        prevProps.controlKey === nextProps.controlKey &&
        prevProps.currentControlValue === nextProps.currentControlValue &&
        prevProps.disabled === nextProps.disabled &&
        prevProps.isHomeschooled === nextProps.isHomeschooled &&
        prevProps.errorMessage === nextProps.errorMessage
    );
});
HighSchoolAutocompleteComponent.displayName = 'HighSchoolAutocompleteComponent';

interface ControlRendererProps {
    ctrl: Attribute;
    formValues: Record<string, string>;
    onChange: (_id: string, value: any) => void;
    errors: { [key: string]: string };
    disabled?: boolean;
    entityName?: string;
    inputDisabled?: boolean;
    dynamicOptions?: string[] | null;
    isLoadingOptions?: boolean;
    estimatedNetPrice?: string;
    onNavigateToSection?: (sectionKey: string) => void;
    tabAttributes?: Record<string, Control[]>;
    activeTab?: string;
    handleConnectWithUs?: () => void;
    isGlobalValid?: boolean;
    handleNext?: () => void;
    loadingNextTab?: boolean;
    handleBack?: () => void;
    isEditingFromSummary?: boolean;
    setIsEditingFromSummary?: React.Dispatch<React.SetStateAction<boolean>>;
    financesDisabledFromEdit?: boolean;
    setFinancesDisabledFromEdit?: React.Dispatch<React.SetStateAction<boolean>>;
    parentDisabledFromEdit?: boolean;
    setParentDisabledFromEdit?: React.Dispatch<React.SetStateAction<boolean>>;
    calculateDerivedAttributesForEntity?: (entityId: string, overrideBody?: any, currentEntityId?: string) => Promise<any>;
    estimateId?: string;
    showErrors?: boolean;
}

// Helper function to truncate long text with ellipses
const truncateText = (text: string, maxLength: number = 90): { truncated: string; isTruncated: boolean } => {
    if (!text || text.length <= maxLength) {
        return { truncated: text, isTruncated: false };
    }
    return {
        truncated: `${text.substring(0, maxLength)}...`,
        isTruncated: true
    };
};

const ControlRenderer: React.FC<ControlRendererProps> = ({
    ctrl,
    formValues,
    onChange: parentOnChange,
    errors,
    disabled = false,
    entityName,
    tabAttributes = {},
    inputDisabled,
    dynamicOptions,
    isLoadingOptions = false,
    estimatedNetPrice,
    onNavigateToSection,
    activeTab,
    handleConnectWithUs,
    isGlobalValid,
    handleNext,
    loadingNextTab,
    handleBack,
    isEditingFromSummary: propIsEditingFromSummary = false,
    setIsEditingFromSummary: propSetIsEditingFromSummary,
    financesDisabledFromEdit: propFinancesDisabledFromEdit,
    setFinancesDisabledFromEdit: propSetFinancesDisabledFromEdit,
    parentDisabledFromEdit: propParentDisabledFromEdit,
    setParentDisabledFromEdit: propSetParentDisabledFromEdit,
    calculateDerivedAttributesForEntity,
    estimateId,
    showErrors = false,
}) => {

    // ============================================================================
    // STATE MANAGEMENT - All hooks must be at the top
    // ============================================================================

    // Local editing state (use parent-provided setter/state if available)
    const [localIsEditingFromSummary, setLocalIsEditingFromSummary] = useState<boolean>(false);
    const isEditingFromSummary = propIsEditingFromSummary ?? localIsEditingFromSummary;
    const setIsEditingFromSummary = propSetIsEditingFromSummary ?? setLocalIsEditingFromSummary;

    // Local forced-finances flag (prefer parent-provided if given)
    const [localFinancesDisabledFromEdit, setLocalFinancesDisabledFromEdit] = useState<boolean>(false);
    const financesDisabledFromEdit = propFinancesDisabledFromEdit ?? localFinancesDisabledFromEdit;
    const setFinancesDisabledFromEdit = propSetFinancesDisabledFromEdit ?? setLocalFinancesDisabledFromEdit;

    // Local flag for parent disabled from independent edit
    const [localParentDisabledFromEdit, setLocalParentDisabledFromEdit] = useState<boolean>(false);
    const parentDisabledFromEdit = propParentDisabledFromEdit ?? localParentDisabledFromEdit;
    const setParentDisabledFromEdit = propSetParentDisabledFromEdit ?? setLocalParentDisabledFromEdit;

    // State for tooltip visibility
    const [showSelectTooltip, setShowSelectTooltip] = useState<boolean>(false);

    // Helper variables
    const controlKey = ctrl._id || ctrl.name;
    const currentControlValue = formValues[controlKey] || '';
    let errorMessage: string | undefined = errors[controlKey];
    const labelText = ctrl.name;
    const placeholderText = `Enter ${labelText.replace('*', '').trim()}`;


    // Debounced scroll-to-first-error: only trigger when errors transition from 0 -> >0
    const prevErrorCountRef = useRef<number>(0);
    const scrollTimeoutRef = useRef<ReturnType<typeof globalThis.setTimeout> | null>(null);

    useEffect(() => {
        const errorKeys = Object.keys(errors);

        // Manage .has-error class on controls so they show red border but no focus ring
        const currentErrorSet = new Set(errorKeys);

        // Remove .has-error from elements that are no longer in errors
        for (const el of Array.from(document.querySelectorAll('.has-error'))) {
            const id = (el as HTMLElement).id;
            if (id && !currentErrorSet.has(id)) {
                el.classList.remove('has-error');
            }
        }

        // Add .has-error to current error elements and ensure they are tabbable for a11y
        // BUT exclude disabled fields
        for (const key of errorKeys) {
            const el = document.getElementById(key);
            if (el) {
                // Check if the element is disabled - if so, skip adding has-error
                const isDisabled = el.hasAttribute('disabled') || (el as HTMLInputElement | HTMLSelectElement).disabled;

                if (!isDisabled) {
                    el.classList.add('has-error');
                    // Use tabindex="0" so elements remain reachable via keyboard
                    // while still allowing programmatic focus when scrolling to errors
                    if (!el.hasAttribute('tabindex')) {
                        el.setAttribute('tabindex', '0');
                    }
                }
            }
        }

        // Filter out disabled fields from errorKeys for scroll-to-error logic
        const validErrorKeys = errorKeys.filter(key => {
            const el = document.getElementById(key);
            if (!el) return false;

            // Skip if element is disabled
            const isDisabled = el.hasAttribute('disabled') || (el as HTMLInputElement | HTMLSelectElement).disabled;
            return !isDisabled
        });

        // Always trigger scroll and focus when there are validation errors after Continue is clicked
        // This ensures focus moves to the first error field on every Continue attempt
        const scheduleScrollToError = () => {
            // debounce to avoid jarring UX when validations fire rapidly
            if (scrollTimeoutRef.current) {
                globalThis.clearTimeout(scrollTimeoutRef.current);
            }
            scrollTimeoutRef.current = globalThis.setTimeout(() => {
                const firstErrorKey = validErrorKeys[0];
                const errorElement = document.getElementById(firstErrorKey);
                if (errorElement) {
                    errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    // focus for screen readers but visible focus ring is suppressed by .has-error
                    errorElement.focus();
                }
                scrollTimeoutRef.current = null;
            }, 200);
        };

        if (validErrorKeys.length > 0 && showErrors) {
            scheduleScrollToError();
        }
        // Also handle the original case for progressive validation feedback
        else if (validErrorKeys.length > 0 && prevErrorCountRef.current === 0) {
            // This handles cases where errors appear during typing/blur events
            scheduleScrollToError();
        }

        prevErrorCountRef.current = validErrorKeys.length;

        return () => {
            if (scrollTimeoutRef.current) {
                globalThis.clearTimeout(scrollTimeoutRef.current);
                scrollTimeoutRef.current = null;
            }
        };
    }, [errors, showErrors]);

    // Get tenant config at top level (React Hooks must be called unconditionally)
    const { tenantConfig, derivedAttributesData } = useTenant();

    // Use ctrl.name as primary identifier (displayName might not always be set)
    const controlDisplayName = ctrl?.name ?? '';

    // Memoize adjusted dynamic options
    const effectiveDynamicOptions = useMemo(() => {
        if (!Array.isArray(dynamicOptions) || !controlDisplayName || !entityName) {
            return dynamicOptions;
        }

        const hasReplacement = getReplacementForControlName(controlDisplayName, entityName);
        if (!hasReplacement) return dynamicOptions;

        try {
            // Map ALL dynamic options through the adjustment function
            return dynamicOptions.map((item: any) =>
                adjustOptionItemForControl(item, controlDisplayName, entityName)
            );
        } catch (error) {
            console.warn('Failed to adjust dynamicOptions:', error);
            return dynamicOptions;
        }
    }, [dynamicOptions, controlDisplayName, entityName]);

    // Blur active input when user clicks Continue-like buttons to remove focus ring
    useEffect(() => {
        const handler = (ev: MouseEvent) => {
            const target = ev.target as HTMLElement | null;
            if (!target) return;
            const continueBtn = target.closest('[data-action="continue"], .continue-button, button[type="submit"][data-action="start"]');
            if (continueBtn) {
                globalThis.setTimeout(() => {
                    const active = document.activeElement as HTMLElement | null;
                    if (active) {
                        const tag = active.tagName?.toLowerCase();
                        if (tag === 'input' || tag === 'textarea' || tag === 'select' || active.getAttribute('role') === 'textbox') {
                            try {
                                active.blur();
                            } catch (err) {
                                // Ignore errors in production but surface them in development for debugging
                                if (process.env.NODE_ENV !== 'production') {
                                    // eslint-disable-next-line no-console
                                    console.warn('Failed to blur active element:', err);
                                }
                            }
                        }
                    }
                }, 10);
            }
        };
        document.addEventListener('click', handler, true);
        return () => document.removeEventListener('click', handler, true);
    }, []);

    // Remove undesired visual highlight on summary accordion headers
    // This runs unconditionally so React Hooks ordering rules are preserved.
    useEffect(() => {
        const selector = '[id^="accordion-header-"]';

        const onFocusIn = (ev: FocusEvent) => {
            const target = ev.target as HTMLElement | null;
            if (!target) return;
            const header = target.closest<HTMLElement>(selector);
            if (!header) return;
            header.style.outline = 'none';
            header.style.boxShadow = 'none';
        };

        const onMouseDown = (ev: MouseEvent) => {
            const target = ev.target as HTMLElement | null;
            if (!target) return;
            const header = target.closest<HTMLElement>(selector);
            if (!header) return;
            requestAnimationFrame(() => {
                header.style.outline = 'none';
                header.style.boxShadow = 'none';
            });
        };

        document.addEventListener('focusin', onFocusIn, true);
        document.addEventListener('mousedown', onMouseDown, true);

        return () => {
            document.removeEventListener('focusin', onFocusIn, true);
            document.removeEventListener('mousedown', onMouseDown, true);
        };
    }, []);

    // Accordion state for Summary entity (always declared)
    const [openSections, setOpenSections] = useState<{ [key: string]: boolean }>({
        'student': false,
        'academic': false,
        'family': false,
        'parent': false,
        'studentFinances': false
    });

    // --- HELPERS: Define after hooks, before rendering logic ---
    // Helper: normalize string keys (remove spaces, punctuation, lowercase)
    const normalizeKey = (s?: string) => {
        if (!s) return '';
        return String(s).toLowerCase().replaceAll(/[^a-z0-9]/g, '');
    };

    // Helper function to get display value safely
    const getDisplayValue = (value: string | null | undefined): string => {
        if (!value || value === '') return '';
        return value;
    };



    // Helper function to format HCRC year for display - shows blank for default/false values
    const formatHcrcYearDisplay = (value: string | null | undefined): string => {
        if (!value || value === '' || value === 'false') return '';
        return value;
    };

    // Helper function to find control by name (case-insensitive)
    const findControlByName = (
        attributes: Control[],
        targetName: string,
        alternativeNames: string[] = []
    ): Control | undefined => {
        // Try exact match first
        const exactMatch = attributes.find(attr => attr.name === targetName);
        if (exactMatch) return exactMatch;

        // Try case-insensitive match
        const normalizedTarget = targetName.toLowerCase();
        const caseInsensitiveMatch = attributes.find(
            attr => attr.name?.toLowerCase() === normalizedTarget
        );
        if (caseInsensitiveMatch) return caseInsensitiveMatch;

        // Try alternative names
        for (const altName of alternativeNames) {
            const normalizedAlt = altName.toLowerCase();
            const altMatch = attributes.find(
                attr => attr.name?.toLowerCase() === normalizedAlt
            );
            if (altMatch) return altMatch;
        }

        // Try partial match
        const partialMatch = attributes.find(
            attr => attr.name?.toLowerCase().includes(normalizedTarget)
        );
        return partialMatch;
    };

    // Helper function to get form value by control
    const getFormValueByControl = React.useCallback((
        attributes: Control[],
        fieldName: string,
        alternativeNames: string[] = []
    ): string => {
        const control = findControlByName(attributes, fieldName, alternativeNames);
        if (!control) return '';
        return formValues[control._id] || '';
    }, [formValues]);

    // Get all attributes from all entities for lookup
    const allEntityAttributes = React.useMemo(() => {
        // Helper to resolve tabAttributes key robustly
        const resolveTabAttrs = (entityName: string): Control[] => {
            if (!tabAttributes) return [];

            // Create a list of possible keys based on the entity name
            const candidates = [
                entityName, // e.g., "FAMILY_INFORMATION"
                entityName.toLowerCase(), // e.g., "family_information"
                entityName.replaceAll('_', '-').toLowerCase(), // e.g., "family-information"
                entityName.replaceAll(/\s+/g, '-').toLowerCase(), // e.g., "family-information" (from spaces)
                entityName.replaceAll('_', ''), // e.g., "FAMILYINFORMATION"
                entityName.replaceAll('_', '').toLowerCase(), // e.g., "familyinformation"
            ];

            for (const key of candidates) {
                if (tabAttributes[key]) {
                    return tabAttributes[key];
                }
            }

            return [];
        };


        const studentInfoAttrs = resolveTabAttrs(ENTITY_NAMES.STUDENT_INFORMATION);
        const studentContactAttrs = resolveTabAttrs(ENTITY_NAMES.STUDENT_CONTACT);
        const academicAttrs = resolveTabAttrs(ENTITY_NAMES.ACADEMIC_SCORES);
        const familyAttrs = resolveTabAttrs(ENTITY_NAMES.FAMILY_INFORMATION);
        const parentFinancesAttrs = resolveTabAttrs(ENTITY_NAMES.PARENT_FINANCES);
        const studentFinancesAttrs = resolveTabAttrs(ENTITY_NAMES.STUDENT_FINANCES);
        const summaryAttrs = resolveTabAttrs(ENTITY_NAMES.SUMMARY);



        return {
            studentInfo: studentInfoAttrs,
            studentContact: studentContactAttrs,
            academic: academicAttrs,
            family: familyAttrs,
            parentFinances: parentFinancesAttrs,
            studentFinances: studentFinancesAttrs,
            summary: summaryAttrs,
            all: [
                ...studentInfoAttrs,
                ...studentContactAttrs,
                ...academicAttrs,
                ...familyAttrs,
                ...parentFinancesAttrs,
                ...studentFinancesAttrs,
                ...summaryAttrs
            ]
        };
    }, [tabAttributes]);

    // Get family attributes for control detection
    const familyAttributes = React.useMemo(() =>
        allEntityAttributes.family || [],
        [allEntityAttributes]
    );

    // HARDCODED: Remove has-error from State field when it's disabled (Non-USA citizenship)
    useEffect(() => {
        const studentAttrs = [
            ...allEntityAttributes.studentInfo,
            ...allEntityAttributes.studentContact
        ];
        const stateControl = studentAttrs.find(c =>
            c.name?.toLowerCase() === COMMON_FIELD_NAMES.STATE.toLowerCase()
        );
        const citizenshipControl = studentAttrs.find(c =>
            c.name?.toLowerCase().includes('citizenship') ||
            c.name === COMMON_FIELD_NAMES.STUDENT_CITIZENSHIP
        );

        if (stateControl && citizenshipControl) {
            const citizenshipValue = formValues[citizenshipControl._id];
            const isUSA = String(citizenshipValue ?? '').toLowerCase() === LOCATION_VALUES.USA_LOWER;

            if (!isUSA) {
                const stateElement = document.getElementById(stateControl._id);
                if (stateElement?.classList.contains('has-error')) {
                    stateElement.classList.remove('has-error');
                }
            }
        }
    }, [formValues, allEntityAttributes]);

    // Find "Connect With Us" control from Summary entity attributes
    const connectWithUsControl = React.useMemo(() => {
        const summaryAttrs = allEntityAttributes.summary || [];
        return summaryAttrs.find(attr =>
            attr.name?.toLowerCase().includes('connect with us') ||
            attr.friendlyName?.toLowerCase().includes('connect with us')
        );
    }, [allEntityAttributes.summary]);

    // Extract URL for "Connect With Us" button, prioritizing defaultValue over constantValue
    const connectWithUsUrl = React.useMemo(() => {
        if (!connectWithUsControl) return '';
        return connectWithUsControl.defaultValue ?? connectWithUsControl.constantValue ?? '';
    }, [connectWithUsControl]);

    // Open contact page in a new tab safely, using URL from "Connect With Us" attribute
    const openContactUs = React.useCallback(() => {
        if (connectWithUsUrl) {
            const newWin = window.open(connectWithUsUrl, '_blank', 'noopener,noreferrer');
            if (newWin) {
                newWin.opener = null;
            }
        }
    }, [connectWithUsUrl]);

    // Extract disclaimer text from Summary attributes
    const disclaimerText = React.useMemo(() => {
        const summaryAttrs = allEntityAttributes.summary || [];

        if (summaryAttrs.length > 0) {
            // Find the attribute with empty name and a description
            // Note: order field may be undefined in tabAttributes, so we don't check it
            const disclaimerAttr = summaryAttrs.find(
                attr => attr.name === '' && attr.description && attr.description.length > 100
            );

            if (disclaimerAttr?.description) {
                return disclaimerAttr.description;
            }
        }

        return '';
    }, [allEntityAttributes]);

    // Find Independent Student control IDs - memoized
    const independentControlIds = useMemo(() => {
        return familyAttributes
            .filter(attr => {
                const name = String(attr?.name || '').toLowerCase();
                return name.includes('independent student') ||
                    name.includes('student status') ||
                    name.includes('are you an independent');
            })
            .map(attr => attr._id);
    }, [familyAttributes]);

    // SAI text input controls (disables both finances)
    const saiTextControlIds = useMemo(() => {
        return familyAttributes
            .filter(attr => {
                const name = String(attr?.name || '').toLowerCase();
                return name.includes('student aid index') ||
                    name.includes('sai') ||
                    name.includes('student aid') ||
                    name.includes('please enter your student aid index');
            })
            .map(attr => attr._id);
    }, [familyAttributes]);

    // Skip option controls (disables both finances)
    const skipControlIds = useMemo(() => {
        return familyAttributes
            .filter(attr => {
                const name = String(attr?.name || '').toLowerCase();
                return name.includes('do not want to calculate') ||
                    name.includes('international citizen') ||
                    name.includes('ineligible for need-based') ||
                    (name.includes('already have') && name.includes('student aid index'));
            })
            .map(attr => attr._id);
    }, [familyAttributes]);

    // Aid calculation radio/checkbox controls
    const aidCalculationControlIds = useMemo(() => {
        return familyAttributes
            .filter(attr => {
                const name = String(attr?.name || '').toLowerCase();
                return name.includes('aid calculation') ||
                    name.includes('choose one of the following') ||
                    name.includes('you may choose to skip');
            })
            .map(attr => attr._id);
    }, [familyAttributes]);

    // Helper function to detect if a field is parent-related (should be disabled for independent students)
    const isParentRelatedField = useCallback((attribute: Control): boolean => {
        if (!attribute) return false;

        // Check multiple sources for parent-related indicators
        const indicators = [
            attribute.name,
            attribute.friendlyName,
            attribute.description,
            attribute.toolTip
        ].filter(Boolean).map(s => String(s).toLowerCase());

        // Keywords that indicate parent-related fields
        const parentKeywords = ['parent', 'custodial'];

        // Keywords that indicate the field should be disabled for independent students
        const employmentKeywords = ['employ', 'work', 'tuition exchange'];

        // Check if field mentions both parent AND employment/benefits
        const hasParentReference = indicators.some(text =>
            parentKeywords.some(keyword => text.includes(keyword))
        );

        const hasEmploymentReference = indicators.some(text =>
            employmentKeywords.some(keyword => text.includes(keyword))
        );

        return hasParentReference && hasEmploymentReference;
    }, []);

    // Dynamically identify parent employment controls without hardcoding names
    const parentEmploymentControlIds = useMemo(() => {
        const results = familyAttributes
            .filter(attr => {
                // Use the helper to detect parent-related employment fields
                const isParentField = isParentRelatedField(attr);

                return isParentField;
            })
            .map(attr => attr._id);

        return results;
    }, [familyAttributes, isParentRelatedField]);

    // ============================================================================
    // DERIVED STATE - Current form values for reactive updates
    // ============================================================================

    const currentIndependentStatus = useMemo(() => {
        const independentAttr = familyAttributes.find(attr =>
            String(attr?.name || '').toLowerCase().includes('independent student')
        );
        if (!independentAttr) return null;
        const value = String(formValues[independentAttr._id] ?? '').toLowerCase();

        return value;
    }, [familyAttributes, formValues]);

    const currentSAIValue = useMemo(() => {
        const saiAttr = familyAttributes.find(attr => {
            const name = String(attr?.name || '').toLowerCase();
            return name.includes('student aid index') && !name.includes('already have');
        });
        if (!saiAttr) return '';
        return String(formValues[saiAttr._id] ?? '').trim();
    }, [familyAttributes, formValues]);

    const currentSkipOptions = useMemo(() => {
        const skipKeywords = ['already have', 'do not want', 'international', 'ineligible'];

        const skipFromCheckbox = skipControlIds.some(controlId => {
            const value = String(formValues[controlId] ?? '').toLowerCase().trim();
            // Check for boolean values OR skip keywords in the text
            const isBooleanSkip = value === 'yes' || value === 'true';
            const hasSkipKeyword = skipKeywords.some(keyword => value.includes(keyword));
            const matches = isBooleanSkip || hasSkipKeyword;

            return matches;
        });

        const skipFromRadio = aidCalculationControlIds.some(controlId => {
            const value = String(formValues[controlId] ?? '').toLowerCase().trim();
            const hasSkipKeyword = skipKeywords.some(keyword => value.includes(keyword));


            return hasSkipKeyword;
        });

        const result = skipFromCheckbox || skipFromRadio;

        return result;
    }, [skipControlIds, aidCalculationControlIds, formValues]);

    // ============================================================================
    // EFFECT - Sync disabled state with current form values
    // ============================================================================

    useEffect(() => {
        // Auto-sync based on current form values
        const shouldDisableFinances = currentSAIValue !== '' && currentSAIValue !== '0';
        const shouldDisableFromSkip = currentSkipOptions;

        if (currentIndependentStatus === 'no') {
            // Independent=No: ALWAYS enable both finance tabs regardless of SAI/skip
            // When dependent, user MUST provide parent/student finance information
            setFinancesDisabledFromEdit(false);
            setParentDisabledFromEdit(false);
        } else if (currentIndependentStatus === 'yes') {
            // Independent=Yes: parent always disabled, Student Finances enabled
            setParentDisabledFromEdit(true);
            // Independent students can always enter Student Finances
            setFinancesDisabledFromEdit(false);

            // Clear parent employment field values when they become disabled
            parentEmploymentControlIds.forEach(controlId => {
                const currentValue = formValues[controlId];

                // Find the attribute to get its default value
                const attribute = familyAttributes.find(attr => attr._id === controlId);
                const defaultValue = attribute?.defaultValue ?? '';

                // Only update if current value is not the default
                if (currentValue && currentValue !== defaultValue) {
                    // Use parentOnChange directly to avoid circular dependency
                    if (typeof parentOnChange === 'function') {
                        parentOnChange(controlId, defaultValue);
                    }
                }
            });
        } else {
            // Independent not set: SAI/skip can disable both
            setParentDisabledFromEdit(shouldDisableFinances || shouldDisableFromSkip);
            setFinancesDisabledFromEdit(shouldDisableFinances || shouldDisableFromSkip);
        }
    }, [
        currentIndependentStatus,
        currentSAIValue,
        currentSkipOptions,
        setFinancesDisabledFromEdit,
        setParentDisabledFromEdit,
        parentEmploymentControlIds,
        familyAttributes,
        formValues,
        parentOnChange
    ]);

    // Helper functions to reduce cognitive complexity of onChange
    const handleIndependentChange = useCallback((normalizedValue: string) => {
        if (normalizedValue === 'no') {
            // ALWAYS enable both finance tabs when Independent Student is "No"
            setFinancesDisabledFromEdit(false);
            setParentDisabledFromEdit(false);
        } else if (normalizedValue === 'yes') {
            // ALWAYS disable Parent Finances only when Independent Student is "Yes"
            // Ensure Student Finances stays enabled even if previously disabled by SAI/skip
            setParentDisabledFromEdit(true);
            setFinancesDisabledFromEdit(false);
        }
    }, [setFinancesDisabledFromEdit, setParentDisabledFromEdit]);

    const handleSAIChange = useCallback((normalizedValue: string) => {
        // For text input SAI fields: check if user entered a numeric value
        // For Yes/No radio/checkbox fields: check if 'yes' was selected
        const isSAIEntered = normalizedValue !== '' &&
            normalizedValue !== '0' &&
            normalizedValue !== 'no';

        if (isSAIEntered && isEditingFromSummary) {
            // SAI text value entered or Yes option selected
            setFinancesDisabledFromEdit(true);
            setParentDisabledFromEdit(false); // Clear parent-only flag
        } else if (normalizedValue === '' || normalizedValue === '0' || normalizedValue === 'no') {
            // Enable finances if SAI cleared or No selected
            setFinancesDisabledFromEdit(false);
        }
    }, [isEditingFromSummary, setFinancesDisabledFromEdit, setParentDisabledFromEdit]);

    const handleSkipChange = useCallback((normalizedValue: string) => {
        // For Yes/No radio/checkbox fields: check if 'yes' was selected
        const isYesChecked = normalizedValue === 'yes' || normalizedValue === 'true';

        if (isYesChecked && isEditingFromSummary) {
            setFinancesDisabledFromEdit(true);
            setParentDisabledFromEdit(false); // Clear parent-only flag
        } else if (normalizedValue === 'no' || normalizedValue === 'false' || normalizedValue === '') {
            // Enable finances if No selected or cleared
            setFinancesDisabledFromEdit(false);
        }
    }, [isEditingFromSummary, setFinancesDisabledFromEdit, setParentDisabledFromEdit]);

    const handleAidCalculationChange = useCallback((normalizedValue: string) => {
        const skipKeywords = [
            'already have my student aid index',
            'already have',
            'do not want to calculate',
            'international citizen',
            'ineligible for need-based'
        ];

        const isSkipOption = skipKeywords.some(keyword =>
            normalizedValue.includes(keyword)
        );

        if (isSkipOption && isEditingFromSummary) {
            setFinancesDisabledFromEdit(true);
            setParentDisabledFromEdit(false); // Clear parent-only flag
        } else if (!isSkipOption && normalizedValue !== '') {
            // User selected non-skip option (e.g., "Calculate my need-based aid")
            setFinancesDisabledFromEdit(false);
        }
    }, [isEditingFromSummary, setFinancesDisabledFromEdit, setParentDisabledFromEdit]);

    // Wrap the parent's onChange to detect independent/SAI/skip changes
    const onChange = useCallback((controlId: string, value: any) => {
        // Check if this is the homeschooled checkbox being checked BEFORE calling parent handler
        const studentAttrs = [
            ...allEntityAttributes.studentInfo,
            ...allEntityAttributes.studentContact
        ];

        const changedControl = studentAttrs.find(c => c._id === controlId);
        const isHomeschooledControl = changedControl?.name?.toLowerCase().includes(COMMON_FIELD_NAMES.HOMESCHOOL);

        if (isHomeschooledControl && value === BOOLEAN_VALUES.TRUE) {
            // Find the High School field
            const highSchoolControl = studentAttrs.find(c => {
                const name = c.name?.toLowerCase() || '';
                return name.includes(COMMON_FIELD_NAMES.HIGH_SCHOOL.toLowerCase()) || (name.includes('school') && !name.includes(COMMON_FIELD_NAMES.HOMESCHOOL));
            });

            if (highSchoolControl) {
                // Clear the high school data from localStorage first
                try {
                    localStorage.removeItem('npc_high_school');
                } catch (error) {
                    console.warn('Failed to remove high school from localStorage:', error);
                }

                // Clear the high school value in form by calling parent handler
                if (typeof parentOnChange === 'function') {
                    parentOnChange(highSchoolControl._id, '');
                }
            }
        }

        // Check if this is the citizenship control being changed to Non-USA in Student Information
        const isCitizenshipControl = (changedControl?.name?.toLowerCase().includes('citizenship') ||
            changedControl?.name === COMMON_FIELD_NAMES.STUDENT_CITIZENSHIP) &&
            (entityName === ENTITY_NAMES.STUDENT_INFORMATION || entityName === 'studentInfo');

        if (isCitizenshipControl) {
            const isUSA = String(value ?? '').toLowerCase() === LOCATION_VALUES.USA_LOWER;

            if (!isUSA) {
                // Find the State field in Student Information
                const stateControl = allEntityAttributes.studentInfo.find(c =>
                    c.name?.toLowerCase() === COMMON_FIELD_NAMES.STATE.toLowerCase()
                );

                if (stateControl) {
                    // Set State field to its default value (or first valid option) when disabled
                    // This prevents validation errors on a disabled field
                    const defaultValue = stateControl.defaultValue ?? 'N/A';
                    if (typeof parentOnChange === 'function') {
                        parentOnChange(stateControl._id, defaultValue);
                    }
                }
            }
        }

        // Call parent handler for the current control
        try {
            if (typeof parentOnChange === 'function') {
                parentOnChange(controlId, value);
            }
        } catch (error) {
            console.error('Error in parent onChange handler:', error);
        }

        const normalizedValue = String(value ?? '').trim().toLowerCase();

        if (independentControlIds.includes(controlId)) {
            handleIndependentChange(normalizedValue);
            return; // Exit early for independent control
        }

        if (saiTextControlIds.includes(controlId)) {
            handleSAIChange(normalizedValue);
            return; // Exit early for SAI control
        }

        if (skipControlIds.includes(controlId)) {
            handleSkipChange(normalizedValue);
            return; // Exit early for skip control
        }

        if (aidCalculationControlIds.includes(controlId)) {
            handleAidCalculationChange(normalizedValue);
        }
    }, [
        parentOnChange,
        independentControlIds,
        saiTextControlIds,
        skipControlIds,
        aidCalculationControlIds,
        handleIndependentChange,
        handleSAIChange,
        handleSkipChange,
        handleAidCalculationChange,
        allEntityAttributes.studentInfo,
        allEntityAttributes.studentContact,
        entityName
    ]);

    // Helper function to mask sensitive values for display
    const maskValue = (value: string | null, type: 'phone' | 'postal') => {
        if (!value) return '';
        const cleanValue = value.replaceAll(/\D/g, ''); // Remove non-digits
        if (type === 'phone' && cleanValue.length === 10) {
            return `(${cleanValue.slice(0, 3)}) ${cleanValue.slice(3, 6)}-${cleanValue.slice(6)}`;
        }
        if (type === 'postal') {
            if (cleanValue.length === 9) {
                return `${cleanValue.slice(0, 5)}-${cleanValue.slice(5)}`;
            }
            if (cleanValue.length === 5) {
                return cleanValue;
            }
            return 'Invalid';
        }
        return value; // Fallback for other cases
    };

    // Helper function to convert boolean values to display text for Summary
    // Uses centralized booleanValueToDisplay utility for consistency
    const formatBooleanForDisplay = (value: string | null | undefined): string => {
        return booleanValueToDisplay(value);
    };

    // Helper function to get display value for dropdown controls
    // Checks in order: 1) API displayValue, 2) localStorage, 3) possibleValues, 4) raw value
    const getDropdownDisplayValueForControl = React.useCallback((
        value: string | null | undefined,
        attributes: Control[],
        fieldName: string,
        alternativeNames: string[] = []
    ): string => {
        if (!value) return '';

        const control = findControlByName(attributes, fieldName, alternativeNames);
        if (!control) return value;

        // PRIORITY 1: Check if the control itself has displayValue from API response
        // This is the most reliable source as it comes directly from the server
        const controlDisplayValue = (control as any)?.displayValue;
        if (controlDisplayValue !== undefined && controlDisplayValue !== null && controlDisplayValue !== '') {
            return String(controlDisplayValue);
        }

        // PRIORITY 2: Special handling for High School field - check localStorage
        const isHighSchoolField = fieldName === 'High School' ||
            alternativeNames.some(alt => alt.includes('high school') || alt === 'school');

        if (isHighSchoolField) {
            // Try to get school name from localStorage using the utility function
            try {
                const storedData = getHighSchoolData();
                if (storedData?.hsName) {
                    // Validate that the stored CEEB matches the current value
                    if (storedData.ceeb && String(storedData.ceeb) === String(value)) {
                        return storedData.hsName;
                    }
                }
            } catch (error) {
                console.warn('Failed to read high school from localStorage:', error);
            }

            // Also check legacy localStorage key as fallback
            try {
                const legacyData = localStorage.getItem('selectedHighSchool');
                if (legacyData) {
                    const parsed = JSON.parse(legacyData);
                    if (parsed?.ceeb && String(parsed.ceeb) === String(value) && parsed.hsName) {
                        return parsed.hsName;
                    }
                }
            } catch (error) {
                console.warn('Failed to read high school from legacy localStorage:', error);
            }
        }

        // If control has static possibleValues, use them
        if (Array.isArray(control.possibleValues) && control.possibleValues.length > 0) {
            const matchingOption = control.possibleValues.find((option: any) => {
                if (typeof option === 'object' && option !== null) {
                    return String(option.value) === String(value);
                }
                return String(option) === String(value);
            });

            if (matchingOption && typeof matchingOption === 'object' && matchingOption.displayValue) {
                return String(matchingOption.displayValue);
            }
        }

        // Fallback: return the raw value
        return value;
    }, []);

    // Extract Student Information data for Summary accordion
    const studentInfoData = React.useMemo(() => {
        const studentAttrs = [
            ...allEntityAttributes.studentInfo,
            ...allEntityAttributes.studentContact
        ];

        // Extract values using control IDs
        const citizenship = getFormValueByControl(
            studentAttrs,
            'Student Citizenship',
            ['Citizenship', 'citizenship']
        );
        const age = getFormValueByControl(
            studentAttrs,
            'Student Age',
            ['Age', 'age']
        );
        const state = getFormValueByControl(
            studentAttrs,
            'State',
            ['state', 'student state']
        );
        const highSchool = getFormValueByControl(
            studentAttrs,
            'High School',
            ['high school', 'school']
        );

        const homeschooled = getFormValueByControl(
            studentAttrs,
            'I was/am homeschooled',
            ['homeschooled', 'home schooled', 'homeschool']
        );

        const hcrcYear = getFormValueByControl(
            studentAttrs,
            'What year would you enter HCRC?',
            ['hcrc year', 'hcrc', 'enter hcrc', 'what year']
        );

        const preferredHousing = getFormValueByControl(
            studentAttrs,
            'Preferred Housing',
            ['preferred housing', 'housing preference', 'housing']
        );

        // Get displayValue for High School dropdown (school name instead of CEEB/ID)
        const highSchoolDisplay = getDropdownDisplayValueForControl(
            highSchool,
            studentAttrs,
            'High School',
            ['high school', 'school']
        );


        // Contact details - Use only studentContact attributes to avoid mixing with student info fields
        const contactAttrs = allEntityAttributes.studentContact || [];
        const contactName = getFormValueByControl(
            contactAttrs,
            'Name',
            ['name', 'contact name', 'student name']
        );
        const lastName = getFormValueByControl(
            contactAttrs,
            'Last Name',
            ['last name', 'surname']
        );

        // Prefer precise control resolution (avoid 'Email Address' matching both Email and Address)
        const contactEmailControl = findControlByName(contactAttrs, 'Email', ['email', 'contact email', 'email address']);
        const contactAddressControl = findControlByName(contactAttrs, 'Address', ['address', 'street', 'street address', 'mailing address']);

        let contactEmail = contactEmailControl ? formValues[contactEmailControl._id] || '' : '';
        let contactAddress = contactAddressControl ? formValues[contactAddressControl._id] || '' : '';

        // If both controls resolved to the same attribute (e.g. only 'Email Address' exists), try to disambiguate
        if (contactEmailControl && contactAddressControl && contactEmailControl._id === contactAddressControl._id) {
            const emailControl = contactAttrs.find(a => /\bemail\b/i.test(a.name || ''));
            const addrControl = contactAttrs.find(a => /\b(address|street|mailing|physical|location)\b/i.test(a.name || '') && !/\bemail\b/i.test(a.name || ''));
            if (emailControl) contactEmail = formValues[emailControl._id] || '';
            if (addrControl) contactAddress = formValues[addrControl._id] || '';
        }

        // Extra heuristic: if address contains an '@' it's probably the email � swap if needed
        if (contactAddress && contactAddress.includes('@') && contactEmail && !contactEmail.includes('@')) {
            const tmp = contactAddress;
            contactAddress = contactEmail;
            contactEmail = tmp;
        }

        const contactPhone = getFormValueByControl(
            contactAttrs,
            'Phone',
            ['phone', 'contact phone', 'telephone']
        );
        const contactCity = getFormValueByControl(
            allEntityAttributes.studentContact,
            'City',
            ['city']
        );
        const contactState = getFormValueByControl(
            allEntityAttributes.studentContact,
            'Contact State',
            ['state', 'contact state']
        );
        const contactZipCode = getFormValueByControl(
            allEntityAttributes.studentContact,
            'Zip Code',
            ['zipcode', 'zip', 'postal code']
        );
        const contactApartment = getFormValueByControl(
            allEntityAttributes.studentContact,
            'Apartment, Unit, Other',
            ['apartment', 'unit', 'apt', 'suite', 'apartment unit other']
        );


        return {
            citizenship: getDisplayValue(citizenship),
            age: getDisplayValue(age),
            state: getDisplayValue(state),
            highSchool: getDisplayValue(highSchoolDisplay),
            homeschooled: formatBooleanForDisplay(homeschooled),
            hcrcYear: formatHcrcYearDisplay(hcrcYear),
            preferredHousing: getDisplayValue(preferredHousing),
            contactDetails: {
                name: getDisplayValue(contactName),
                lastName: getDisplayValue(lastName),
                email: getDisplayValue(contactEmail),
                phone: maskValue(contactPhone, 'phone'),
                address: getDisplayValue(contactAddress),
                apartment: getDisplayValue(contactApartment),
                city: getDisplayValue(contactCity),
                state: getDisplayValue(contactState),
                zipCode: maskValue(contactZipCode, 'postal')
            }
        };
    }, [allEntityAttributes, getFormValueByControl, getDropdownDisplayValueForControl, formValues]);


    // Extract Family Information data for Summary accordion
    const familyInfoData = React.useMemo(() => {
        const familyAttrs = allEntityAttributes.family || [];


        // Extract fields using exact API field names from the response
        const studentAidPreference = getFormValueByControl(
            familyAttrs,
            'Student Aid Preference',
            ['Student Aid Preference']
        );

        const studentAidIndex = getFormValueByControl(
            familyAttrs,
            'Student Aid Preference',
            ['Student Aid Preference']
        );

        const yourStudentAidIndex = getFormValueByControl(
            familyAttrs,
            'Your Student Aid Index',
            ['Your Student', 'our Student Aid', 'Your Student Aid Index']
        );

        const independentStudent = getFormValueByControl(
            familyAttrs,
            'Are You an Independent Student?',
            ['StudentStatus', 'independent student', 'student status']
        );

        const householdSize = getFormValueByControl(
            familyAttrs,
            'Number of People in Your Household?',
            ['HouseHoldMembers', 'household size', 'household members']
        );

        const householdMembersInCollege = getFormValueByControl(
            familyAttrs,
            'Number of Household Members in College',
            ['TotalHouseHoldMembersInCollege', 'members in college', 'students in college']
        );

        const parentMaritalStatus = getFormValueByControl(
            familyAttrs,
            'Parent Marital Status',
            ['ParentMaritalStatus', 'marital status', 'parent status']
        );

        const parentAge = getFormValueByControl(
            familyAttrs,
            'Age of Oldest Parent (Years)',
            ['ParentAge', 'parent age', 'oldest parent age']
        );


        return {
            studentAidPreference: getDisplayValue(studentAidPreference),
            studentAidIndex: getDisplayValue(studentAidIndex),
            yourStudentAidIndex: getDisplayValue(yourStudentAidIndex),
            independentStudent: getDisplayValue(independentStudent),
            householdSize: getDisplayValue(householdSize),
            numberOfStudents: getDisplayValue(householdMembersInCollege),
            parentMaritalStatus: getDisplayValue(parentMaritalStatus),
            ageOfOldestParent: getDisplayValue(parentAge)
        };
    }, [allEntityAttributes, getFormValueByControl]);

    // Compute shouldSkip for Summary accordion
    const shouldSkipFinances = React.useMemo(() => {
        const familyAttrs = allEntityAttributes.family || [];
        return shouldSkipToSummary(familyAttrs, formValues);
    }, [allEntityAttributes.family, formValues]);

    // Determine the reason for skipping finances to display appropriate message
    const skipFinancesReason = React.useMemo(() => {
        if (!shouldSkipFinances) return null;

        const familyAttrs = allEntityAttributes.family || [];

        // Check if SAI was provided (fallback/legacy)
        const saiAttribute = familyAttrs.find(attr =>
            attr.name === 'Your Student Aid Index'
        );
        if (saiAttribute && formValues[saiAttribute._id]?.trim()) {
            return 'sai';
        }

        return 'opted-out';
    }, [shouldSkipFinances, allEntityAttributes.family, formValues]);

    // Determine whether parent/student finance entities actually have values filled
    const parentAttrs = React.useMemo(() =>
        allEntityAttributes.parentFinances || [],
        [allEntityAttributes]
    );
    const studentAttrs = React.useMemo(() =>
        allEntityAttributes.studentFinances || allEntityAttributes.studentInfo || [],
        [allEntityAttributes]
    );
    const parentHasData = React.useMemo(() => {
        return parentAttrs.some(attr => {
            const v = formValues?.[attr._id];
            return v !== undefined && v !== null && String(v).trim() !== '';
        });
    }, [parentAttrs, formValues]);
    const studentHasData = React.useMemo(() => {
        return studentAttrs.some(attr => {
            const v = formValues?.[attr._id];
            return v !== undefined && v !== null && String(v).trim() !== '';
        });
    }, [studentAttrs, formValues]);

    
    // Helper function to format currency
    const formatCurrency = (value: string): string => {
        if (!value || value === '') return value;
        // Check if value has + suffix before formatting
        const hasPlus = value.endsWith('+');
        // Extract the first valid number (with optional leading minus and decimal)
        const match = /-?\d+(\.\d+)?/.exec(value);
        if (!match) return value;
        const numValue = Number.parseFloat(match[0]);
        if (Number.isNaN(numValue)) return value;

        const formatted = new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
        }).format(numValue);

        // Preserve + suffix if present
        return hasPlus ? `${formatted}+` : formatted;
    };


    // Helper: resolve enabling attribute current value from formValues robustly
    const resolveEnablingCurrent = useCallback((enAttr?: string): any => {
        if (!enAttr || !formValues) return undefined;

        // First, try to find the control by name in allEntityAttributes to get its _id
        let controlId: string | undefined;
        Object.values(allEntityAttributes).forEach((attrs) => {
            if (Array.isArray(attrs)) {
                const foundAttr = attrs.find(a => a.name === enAttr || a.friendlyName === enAttr);
                if (foundAttr) {
                    controlId = foundAttr._id;
                }
            }
        });

        // If we found the control ID, try to get its value
        if (controlId && formValues.hasOwnProperty(controlId)) {
            return formValues[controlId];
        }

        // Fallback: direct lookup by attribute name or id
        if (formValues.hasOwnProperty(enAttr)) return formValues[enAttr];
        // try id-like lookup
        if (formValues.hasOwnProperty(String(enAttr))) return formValues[String(enAttr)];
        // heuristic: normalized match across keys (e.g., "Athlete Status" -> key like "athletestatus" or "Athlete Status")
        const target = normalizeKey(enAttr);
        for (const k of Object.keys(formValues)) {
            if (normalizeKey(k) === target) return formValues[k];
            // also try if control names were used as "Name (ID)" etc.
            if (normalizeKey(String(formValues[k])) === target) {
                return formValues[k];
            }
        }

        // Debug logging for unresolved enabling attributes (can help diagnose production issues)
        if (process.env.NODE_ENV !== 'production') {
            console.warn(`Could not resolve enablingAttribute: "${enAttr}" for control: "${ctrl.name}"`);
        }

        return undefined;
    }, [formValues, allEntityAttributes, ctrl.name]);

    // Extract the current value of the enabling attribute to trigger re-evaluation
    const enablingAttributeCurrentValue = useMemo(() => {
        if (!ctrl?.enablingAttribute && !ctrl?.enablingFormula) return undefined;
        return resolveEnablingCurrent(ctrl.enablingAttribute);
        // eslint-disable-next-line react-hooks/exhaustive-deps -- formValues is intentionally included to ensure production builds re-evaluate when enabling field changes
    }, [ctrl?.enablingAttribute, ctrl?.enablingFormula, resolveEnablingCurrent, formValues]);

    // Memoized: check if control is enabled based on enablingAttribute/enablingValue/enablingFormula
    // Treat undefined/unset enabling attribute as enabled-by-default to avoid
    // disabling controls before the user has interacted with the form.
    const isEnabled = useMemo((): boolean => {
        // Check if this is a parent employment field
        const isParentEmploymentField = parentEmploymentControlIds.includes(ctrl._id);

        // If independent student is "Yes", disable parent employment fields
        if (isParentEmploymentField && currentIndependentStatus === 'yes') {
            return false;
        }

        // If independent student is "No", ensure parent employment fields are enabled
        if (isParentEmploymentField && currentIndependentStatus === 'no') {
            return true;
        }

        // HARDCODED: State field enabling logic (works in production deployment)
        // State field should be enabled only when Student Citizenship is "USA"
        // This logic only applies to the State field in Student Information, not Student Contact
        const isStateField = ctrl.name?.toLowerCase() === COMMON_FIELD_NAMES.STATE.toLowerCase() &&
            (entityName === ENTITY_NAMES.STUDENT_INFORMATION || entityName === 'studentInfo');

        if (isStateField) {
            const studentAttrs = [
                ...allEntityAttributes.studentInfo,
                ...allEntityAttributes.studentContact
            ];

            const citizenshipControl = studentAttrs.find(c =>
                c.name?.toLowerCase().includes('citizenship') ||
                c.name === COMMON_FIELD_NAMES.STUDENT_CITIZENSHIP
            );

            if (citizenshipControl) {
                const citizenshipValue = formValues[citizenshipControl._id];
                const isUSA = String(citizenshipValue ?? '').toLowerCase() === LOCATION_VALUES.USA_LOWER;
                return isUSA; // Enable only when citizenship is USA
            }
            return false; // Disable by default if citizenship not found
        }

        // HARDCODED: High School field enabling logic (works in production deployment)
        // High School field should be disabled when homeschool checkbox is checked
        // This logic only applies to the High School field in Student Information, not fields in Academic Scores
        const isHighSchoolField = ctrl.name?.toLowerCase() === COMMON_FIELD_NAMES.HIGH_SCHOOL.toLowerCase() &&
            (entityName === ENTITY_NAMES.STUDENT_INFORMATION || entityName === 'studentInfo');

        if (isHighSchoolField) {
            const studentAttrs = [
                ...allEntityAttributes.studentInfo,
                ...allEntityAttributes.studentContact
            ];

            const homeschoolControl = studentAttrs.find(c => c.name?.toLowerCase().includes(COMMON_FIELD_NAMES.HOMESCHOOL));
            if (homeschoolControl) {
                const homeschoolValue = formValues[homeschoolControl._id];
                const isHomeschooled = homeschoolValue === BOOLEAN_VALUES.TRUE ||
                    homeschoolValue === BOOLEAN_VALUES.TRUE_LOWER ||
                    homeschoolValue === BOOLEAN_VALUES.YES ||
                    homeschoolValue === BOOLEAN_VALUES.YES_LOWER;
                if (isHomeschooled) {
                    return false; // Disable High School field when homeschool is checked
                }
            }
        }

        // GPA fields (Your High School GPA and GPA out of) should be disabled when "I Will Not Enter a GPA" is selected
        const isGPAInputField = (
            ctrl.name?.toLowerCase().includes('your high school gpa') ||
            ctrl.name?.toLowerCase().includes('gpa out of') ||
            ctrl.friendlyName?.toLowerCase() === 'userinputgpa' ||
            ctrl.friendlyName?.toLowerCase() === 'gpaoutof'
        ) && (entityName === ENTITY_NAMES.ACADEMIC_SCORES || entityName === 'AcademicScores' || entityName === 'academic');

        if (isGPAInputField) {
            // GPA preference control is on Student Information page, not Academic Scores
            const studentAttrs = [
                ...allEntityAttributes.studentInfo,
                ...allEntityAttributes.studentContact
            ];

            // Find the GPA preference control
            const gpaPreferenceControl = studentAttrs.find((c: Control) =>
                c.friendlyName?.toLowerCase() === 'no_gpa' ||
                c.name?.toLowerCase().includes('will you enter') ||
                c.name?.toLowerCase().includes('weighted or unweighted')
            );

            if (gpaPreferenceControl) {
                const preferenceValue = formValues[gpaPreferenceControl._id];
                const isNoGPA = String(preferenceValue ?? '').trim() === 'I Will Not Enter a GPA';

                if (isNoGPA) {
                    return false; // Disable GPA fields when "I Will Not Enter a GPA" is selected
                }
            }
        }

        // Original enabling logic for other fields
        // Skip early return if we have an enablingFormula, even if enablingAttribute is empty
        if (!ctrl?.enablingAttribute) return true; // No enabling condition

        // Use the memoized value that triggers re-evaluation
        const enablingCurrent = enablingAttributeCurrentValue;

        // If not yet set, behavior depends on whether we have a formula or simple value check
        if (enablingCurrent === undefined || enablingCurrent === null || enablingCurrent === '') {
            // For formula-based conditions, hide by default until condition is met

            if (ctrl.enablingFormula) {

                return false;
            }
            return true;
        }

        // If enablingFormula is present, evaluate it instead of simple equality
        if (ctrl.enablingFormula) {
            const formula = ctrl.enablingFormula.trim();

            // Check if formula contains JavaScript logical operators (|| or &&)
            if (formula.includes('||') || formula.includes('&&')) {
                // Handle JavaScript-style expressions like "no_gpa === Weighted || no_gpa === Unweighted"
                // Extract variable name from the formula (e.g., "no_gpa" from "no_gpa === Weighted")
                // Safe regex: \S+ for the identifier (no spaces) eliminates overlap with \s* (ReDoS fix)
                const firstConditionMatch = /^(\S+)\s*(?:===|!==|==|!=)/.exec(formula);
                const varName = firstConditionMatch ? firstConditionMatch[1].trim() : ctrl.enablingAttribute;

                // Resolve the current value using the variable name from the formula
                const formulaEnablingCurrent = resolveEnablingCurrent(varName);
                const currentValueStr = String(formulaEnablingCurrent ?? '').trim();


                // Split by OR (||) operators first
                const orConditions = formula.split('||').map(c => c.trim());

                let result = false;
                for (const orCondition of orConditions) {
                    // Split each OR condition by AND (&&) operators
                    const andConditions = orCondition.split('&&').map(c => c.trim());
                    let andResult = true;

                    for (const condition of andConditions) {
                        // Parse condition like "no_gpa === Weighted"
                        // Safe regex: \S+ for the identifier (no spaces) eliminates overlap with \s* (ReDoS fix)
                        const condMatch = /^(\S+)\s*(===|!==|==|!=)\s*(.{1,500})$/.exec(condition);
                        if (condMatch) {
                            const leftSide = condMatch[1].trim();
                            const operator = condMatch[2];
                            const rightSide = condMatch[3].trim();

                            // If left side matches our variable name, compare with current value
                            if (leftSide === varName) {
                                const expectedValue = rightSide;
                                const actualValue = currentValueStr;

                                let conditionMet = false;
                                if (operator === '===' || operator === '==') {
                                    conditionMet = actualValue === expectedValue;
                                } else if (operator === '!==' || operator === '!=') {
                                    conditionMet = actualValue !== expectedValue;
                                }

                                andResult = andResult && conditionMet;
                            }
                        }
                    }

                    result = result || andResult;
                }

                return result;
            }

            const currentValueStr = String(enablingCurrent).trim();

            // Parse formula: extract operator and value (e.g., ">=2" -> operator: ">=", value: "2")
            const formulaMatch = /^(>=|<=|>|<|=|!=)(.+)$/.exec(formula);

            if (!formulaMatch) {
                console.warn(`Invalid enablingFormula: ${formula}`);
                return true; // Default to enabled if formula is invalid
            }

            const operator = formulaMatch[1];
            const formulaValueStr = formulaMatch[2].trim();

            // Handle special case: "4+" means ">= 4"
            const currentValue = currentValueStr.replace('+', '');
            const formulaValue = formulaValueStr.replace('+', '');

            // Try numeric comparison first
            const currentNum = parseFloat(currentValue);
            const formulaNum = parseFloat(formulaValue);

            if (!isNaN(currentNum) && !isNaN(formulaNum)) {
                // Numeric comparison
                switch (operator) {
                    case '>=':
                        return currentNum >= formulaNum;
                    case '<=':
                        return currentNum <= formulaNum;
                    case '>':
                        return currentNum > formulaNum;
                    case '<':
                        return currentNum < formulaNum;
                    case '=':
                        return currentNum === formulaNum;
                    case '!=':
                        return currentNum !== formulaNum;
                    default:
                        console.warn(`Unknown operator: ${operator}`);
                        return true;
                }
            }

            // Fallback to string comparison if not numeric
            const normalCurrent = currentValue.toLowerCase();
            const normalFormula = formulaValue.toLowerCase();

            switch (operator) {
                case '=':
                    return normalCurrent === normalFormula;
                case '!=':
                    return normalCurrent !== normalFormula;
                default:
                    console.warn(`Non-numeric comparison not supported for operator: ${operator}`);
                    return true;
            }
        }

        // Robust comparison (handle booleans/strings): normalize both sides
        // Trim whitespace and handle escaped newlines (\n, \r\n, etc.)
        const normalCurrent = String(enablingCurrent)
            .replace(/\\n/g, '\n')  // Replace escaped \n with actual newline
            .replace(/\\r/g, '\r')  // Replace escaped \r with actual carriage return
            .trim()
            .toLowerCase();
        const normalExpected = String(ctrl.enablingValue ?? '')
            .replace(/\\n/g, '\n')
            .replace(/\\r/g, '\r')
            .trim()
            .toLowerCase();
        const result = normalCurrent === normalExpected;

        return result;
    }, [ctrl._id, ctrl.name, ctrl.friendlyName, ctrl.enablingAttribute, ctrl.enablingValue, ctrl.enablingFormula, parentEmploymentControlIds, currentIndependentStatus, enablingAttributeCurrentValue, resolveEnablingCurrent, allEntityAttributes, formValues, entityName]);

    // Effective disabled state is driven by the parent (EntityAttributesSection)
    // The parent calculates per-control disabled flags using the canonical disableLogic
    // and passes it in via the `disabled` prop. We also respect the optional
    // inputDisabled flag for additional local disabling.
    // Additionally, apply dynamic enabling logic based on enablingAttribute/enablingValue.
    const effectiveDisabled = !!disabled || !!inputDisabled || !isEnabled;

    // Keep the original isRequired for display purposes (to show the asterisk)
    // But only require validation when the field is enabled
    const effectiveIsRequired = ctrl.isRequired;
    const effectiveRequiredForValidation = ctrl.isRequired && isEnabled;

    // Create an effective control object with overridden isRequired when disabled
    // This ensures parent validation logic doesn't require disabled fields
    const effectiveCtrl = useMemo(() => {
        if (!isEnabled && ctrl.isRequired) {
            return { ...ctrl, isRequired: false };
        }
        return ctrl;
    }, [ctrl, isEnabled]);

    // Override errorMessage when field is disabled but API still sends isRequired: true
    // This prevents validation errors from showing on disabled fields
    if (!isEnabled && errorMessage) {
        errorMessage = undefined;
    }

    // Helper: renderLabelWithTooltip
    const renderLabelWithTooltip = (id?: string, text?: string, isRequired?: boolean | null, tooltipText?: string | null, additionalClasses = '') => {
        const displayId = id ?? ctrl._id;
        const displayText = text ?? labelText;
        const displayRequired = isRequired ?? false;
        const displayTooltip = (tooltipText ?? ctrl.toolTip ?? '').trim();
        const hasTooltip = !!displayTooltip;
        const tooltipId = `${displayId}-tooltip`;

        // Helper to render text with newline support
        const renderTextWithNewlines = (inputText: string) => {
            if (!inputText) return null;

            // The API returns literal \\n (two characters: backslash + n) instead of actual newlines
            // Split on the literal string '\\n'
            const parts = inputText.split('\\n');

            return (
                <>
                    {parts.map((part, index) => (
                        <React.Fragment key={part || `br-${index}`}>
                            {part}
                            {index < parts.length - 1 && <br />}
                        </React.Fragment>
                    ))}
                </>
            );
        };

        return (
            <label htmlFor={displayId} className={`block mb-1 font-medium text-gray-700 ${additionalClasses}`}>
                <div className="flex items-center justify-between w-full">
                    <span>
                        {renderTextWithNewlines(displayText)}
                        {displayRequired ? ' *' : ''}
                    </span>
                    {hasTooltip && (
                        <div className="relative flex items-center">
                            <Tooltip id={tooltipId} message={displayTooltip} position="top">
                                <span
                                    aria-label={`Help for ${displayText}`}
                                    className="text-black inline-flex items-center justify-center w-5 h-5 rounded-full border border-gray-600 bg-white text-xs font-medium"
                                >
                                    ?
                                </span>
                            </Tooltip>
                        </div>
                    )}
                </div>
            </label>
        );
    };

    // Helper: renderCheckbox
    const renderCheckbox = (label: string, isRequired: boolean | null) => (
        <div className="flex items-center space-x-2 mb-4">
            <input
                type="checkbox"
                id={ctrl._id}
                name={ctrl.name}
                checked={
                    formValues[ctrl._id] === BOOLEAN_VALUES.TRUE ||
                    formValues[ctrl._id] === BOOLEAN_VALUES.TRUE_LOWER ||
                    formValues[ctrl._id] === BOOLEAN_VALUES.YES ||
                    formValues[ctrl._id] === BOOLEAN_VALUES.YES_LOWER
                }
                onChange={(e) => {
                    // Store "True" or "False" for internal state
                    // This will be converted to displayValue in the payload
                    onChange(ctrl._id, e.target.checked ? BOOLEAN_VALUES.TRUE : BOOLEAN_VALUES.FALSE);
                }}
                className="form-checkbox h-5 w-5 "
                disabled={effectiveDisabled}
            />
            {renderLabelWithTooltip(ctrl._id, label, isRequired, ctrl.toolTip, 'cursor-pointer')}
        </div>
    );

    // Helper: handleGPAKeyInput - Function to handle GPA input key codes
    const handleGPAKeyInput = (e: React.KeyboardEvent<HTMLInputElement>, controlKey: string) => {
        // Check for minus or plus key and clear the text box
        if (e.key === '-' || e.key === '+') {
            // Clear the input field by setting value to empty string
            onChange(controlKey, '');
            // Prevent the default behavior to stop the minus from being entered
            e.preventDefault();
        }
    };

    // Helper: renderTextInput
    const renderTextInput = (type: string, placeholder: string, inputDisabled?: boolean) => {
        const isInputDisabled = effectiveDisabled || !!inputDisabled;

        // Check if this is a Parent Finances or Student Finances control
        const isFinanceControl = entityName === ENTITY_NAMES.PARENT_FINANCES || entityName === ENTITY_NAMES.STUDENT_FINANCES;

        // Check if this is a Student Aid Index control
        const isStudentAidIndex = ctrl.name?.toLowerCase().includes('student aid index') ||
            ctrl.name?.toLowerCase().includes('sai');

        // Check if this is a name field (first name, last name, etc.)
        const isNameField = ctrl.name?.toLowerCase().includes('name') ||
            ctrl.name?.toLowerCase().includes('fname') ||
            ctrl.name?.toLowerCase().includes('lname');

        // Check if this is an address field
        const isAddressField = ctrl.name?.toLowerCase().includes('address') ||
            ctrl.name?.toLowerCase().includes('home address') ||
            ctrl.name?.toLowerCase().includes('street');

        // Check if this is an apartment/unit field
        const isApartmentField = ctrl.name?.toLowerCase().includes('apartment') ||
            ctrl.name?.toLowerCase().includes('unit') ||
            ctrl.name?.toLowerCase().includes('apt') ||
            ctrl.name?.toLowerCase().includes('suite');

        // Check if this is a city field
        const isCityField = ctrl.name?.toLowerCase().includes('city');

        // Check if this is a GPA field
        const isGPAField = ctrl.name?.toLowerCase().includes('gpa') ||
            ctrl.name?.toLowerCase().includes('grade point average');

        // Identify GPA scale field for possibleMaxValue binding
        const isGPAScaleField = isGPAField && (
            ctrl.name?.toLowerCase().includes('gpa out of') ||
            ctrl.friendlyName?.toLowerCase().includes('gpaoutof') ||
            ctrl.name?.toLowerCase().includes('gpa scale')
        );

        // Format value with $ prefix and comma separators for finance controls
        const displayValue = isFinanceControl && formValues[controlKey]
            ? `$${Number(formValues[controlKey]).toLocaleString()}`
            : (formValues[controlKey] || '');

        // Determine maxLength based on field type
        let maxLength: number | undefined;
        if (isGPAField) {
            // GPA scale fields need more characters (e.g., "100.00")
            maxLength = isGPAScaleField ? 6 : 2;
        } else if (isStudentAidIndex) {
            maxLength = 9;
        } else if (isNameField) {
            maxLength = 50;
        } else if (isAddressField) {
            maxLength = 100;
        } else if (isApartmentField) {
            maxLength = 30;
        } else if (isCityField) {
            maxLength = 100;
        }

        let stepValue: string | undefined;
        if (isGPAField) {
            stepValue = "any";
        } else if (ctrl.dataType === 'Real') {
            stepValue = '0.01';
        } else if (ctrl.dataType === 'Integer') {
            stepValue = '1';
        }

        return (
            <>
                <input
                    type={type}
                    id={controlKey}
                    name={ctrl.name}
                    value={displayValue}
                    maxLength={maxLength}
                    min={isGPAField ? String(ctrl.possibleMinValue ?? 0) : undefined}
                    max={isGPAScaleField && ctrl.possibleMaxValue ? String(ctrl.possibleMaxValue) : undefined}
                    step={stepValue}
                    onChange={(e) => {

                        // Restrict Student Aid Index: 8 digits for both positive and negative
                        if (isStudentAidIndex) {
                            let value = e.target.value;

                            // Block multiple minus signs
                            const minusCount = value.split('-').length - 1;
                            if (minusCount > 1) {
                                return; // Block if multiple minus signs
                            }

                            // Allow only digits and minus sign
                            value = value.replace(/[^0-9-]/g, '');

                            // Handle negative sign - only allow at the beginning
                            const isNegative = value.startsWith('-');
                            const digitsOnly = value.replace(/-/g, '');

                            // Check digit limits: 8 digits for both positive and negative
                            if (digitsOnly.length <= 8) {
                                const finalValue = isNegative ? '-' + digitsOnly : digitsOnly;
                                onChange(controlKey, finalValue);
                            }
                            return;
                        }

                        // Restrict name fields to 50 characters
                        if (isNameField && e.target.value.length > 50) {
                            return; // Don't update if exceeds 50 characters
                        }

                        // Restrict address fields to 100 characters
                        if (isAddressField && e.target.value.length > 100) {
                            return; // Don't update if exceeds 100 characters
                        }

                        // Restrict apartment fields to 30 characters
                        if (isApartmentField && e.target.value.length > 30) {
                            return; // Don't update if exceeds 30 characters
                        }

                        // Restrict city fields to 100 characters
                        if (isCityField && e.target.value.length > 100) {
                            return; // Don't update if exceeds 100 characters
                        }

                        // Restrict city fields to 100 characters

                        // Handle GPA field validation
                        if (isGPAField) {
                            let value = e.target.value;

                            // Allow only digits and decimal point (no negative sign)
                            value = value.replace(/[^0-9.]/g, '');

                            // Ensure only one decimal point
                            const decimalCount = value.split('.').length - 1;
                            if (decimalCount > 1) {
                                return; // Don't update if multiple decimal points
                            }

                            // Allow empty string or just "." for in-progress typing
                            if (value === '' || value === '.') {
                                onChange(controlKey, value);
                                return;
                            }

                            // Allow up to 2 decimal places
                            if (value.includes('.')) {
                                const parts = value.split('.');
                                if (parts[1] && parts[1].length > 2) {
                                    return; // Block more than 2 decimal places
                                }
                            }

                            const numericValue = Number.parseFloat(value);

                            // For regular GPA field (not scale), validate against possibleMinValue (default to 0)
                            if (!isGPAScaleField && !Number.isNaN(numericValue)) {
                                const minValue = ctrl.possibleMinValue ?? 0;
                                // Don't allow completed numbers < minValue (but allow partial input while typing)
                                if (numericValue < minValue && !value.endsWith('.')) {
                                    return; // Block if complete number is less than minimum allowed
                                }
                            }

                            // For "GPA out of" field, prevent values > 100
                            // For "GPA out of" field, enforce range from API response (dynamic)
                            if (isGPAScaleField && !Number.isNaN(numericValue)) {
                                // Only validate if API provides min/max values (dynamic)
                                if (ctrl.possibleMinValue !== undefined && ctrl.possibleMaxValue !== undefined) {
                                    // Only block if value exceeds maximum during typing
                                    // Minimum validation is handled on blur/submit to allow partial input
                                    if (numericValue > ctrl.possibleMaxValue) {
                                        return;
                                    }
                                }
                            }

                            onChange(controlKey, value);
                            return;
                        }

                        // Remove $ prefix and commas when storing the value
                        let rawValue = isFinanceControl
                            ? e.target.value.replace(/^\$/, '').replaceAll(',', '')
                            : e.target.value;
                        // For Real dataType, sanitize to allow only decimal numbers
                        if (ctrl.dataType === 'Real') {
                            rawValue = rawValue.replace(/[^0-9.-]/g, '');
                            // For GPA fields, allow any decimal value (no upper limit restriction)
                            // For other Real fields, validate decimal format
                            if (!isGPAField) {
                                // Allow decimal pattern with up to 2 decimal places
                                if (rawValue !== '' && rawValue !== '.' && rawValue !== '0.' && !/^\d*\.?\d{0,2}$/.test(rawValue)) {
                                    return; // Don't update if invalid
                                }
                            }
                        } else if (ctrl.dataType === 'Integer') {

                            rawValue = rawValue.replace(/[^0-9-]/g, '');
                            // Ensure it's a valid integer
                            if (rawValue !== '' && rawValue !== '-' && !/^-?\d+$/.test(rawValue)) {
                                return; // Don't update if invalid
                            }
                        }

                        // Apply validation for numeric fields with min/max constraints
                        // Skip GPA fields as they have their own validation logic above
                        if (!isGPAField && (ctrl.dataType === 'Integer' || ctrl.dataType === 'Real') && rawValue !== '' && rawValue !== '-' && rawValue !== '.') {
                            // Enforce max value during input if possibleMaxValue exists
                            if (ctrl.possibleMaxValue !== undefined && ctrl.possibleMaxValue !== null) {
                                const numericValue = parseFloat(rawValue);
                                if (!isNaN(numericValue) && numericValue > ctrl.possibleMaxValue) {
                                    // Don't allow input that exceeds max value
                                    return;
                                }
                            }
                        }

                        onChange(controlKey, rawValue);
                    }}
                    onBlur={(e) => {
                        // Validate on blur for numeric fields with min/max constraints
                        if (!isGPAField && (ctrl.dataType === 'Integer' || ctrl.dataType === 'Real')) {
                            const value = e.target.value;
                            if (value && value !== '' && value !== '-' && value !== '.') {
                                const validationResult = validateNumericAttribute(value, ctrl);
                                if (!validationResult.isValid && validationResult.errorMessage) {
                                    // Display validation error
                                    console.error('Validation error:', validationResult.errorMessage);
                                    // You can set an error state here if needed
                                    // For example: setFieldError(controlKey, validationResult.errorMessage);
                                }
                            }
                        }
                    }}
                    onKeyDown={(e) => {
                        // Handle GPA field key input - clear text box if minus key is pressed
                        if (isGPAField) {
                            handleGPAKeyInput(e, controlKey);
                        }
                    }}
                    // Placeholder should not contain "$"; only displayed values should show currency prefix
                    placeholder={placeholder}
                    className={`w-full p-2 border rounded-md ${errorMessage && !isInputDisabled ? 'border-red-500' : 'border border-gray-600'}${isInputDisabled ? ' bg-gray-100' : ''}`}
                    disabled={isInputDisabled}
                    autoComplete="off"
                />
                {/* Show range hint for numeric fields with min/max constraints */}
                {!isGPAField && (ctrl.dataType === 'Integer' || ctrl.dataType === 'Real') &&
                    (ctrl.possibleMinValue !== undefined || ctrl.possibleMaxValue !== undefined) && (
                        <p className="text-gray-500 text-xs mt-1">
                            {(() => {
                                if (ctrl.possibleMinValue !== undefined && ctrl.possibleMaxValue !== undefined) return ``;
                                if (ctrl.possibleMinValue !== undefined) return `Minimum: ${ctrl.possibleMinValue.toLocaleString()}`;
                                return `Maximum: ${ctrl.possibleMaxValue!.toLocaleString()}`;
                            })()}
                        </p>
                    )}
            </>
        );
    };

    // Helper: renderMaskedTextInput
    const renderMaskedTextInput = (mask: string, placeholder?: string) => {
        const isInputDisabled = effectiveDisabled || !!inputDisabled;

        // Check if this is a Parent Finances or Student Finances control
        const isFinanceControl = entityName === ENTITY_NAMES.PARENT_FINANCES || entityName === ENTITY_NAMES.STUDENT_FINANCES;

        // Check if this is a name field and set maxLength
        const isNameField = ctrl.name?.toLowerCase().includes('name') ||
            ctrl.name?.toLowerCase().includes('fname') ||
            ctrl.name?.toLowerCase().includes('lname');

        // Check if this is an address field
        const isAddressField = ctrl.name?.toLowerCase().includes('address') ||
            ctrl.name?.toLowerCase().includes('home address') ||
            ctrl.name?.toLowerCase().includes('street');

        // Check if this is an apartment/unit field
        const isApartmentField = ctrl.name?.toLowerCase().includes('apartment') ||
            ctrl.name?.toLowerCase().includes('unit') ||
            ctrl.name?.toLowerCase().includes('apt') ||
            ctrl.name?.toLowerCase().includes('suite');

        // Check if this is a city field
        const isCityField = ctrl.name?.toLowerCase().includes('city');

        let maxLength: number | undefined;
        if (isNameField) {
            maxLength = 50;
        } else if (isAddressField) {
            maxLength = 100;
        } else if (isApartmentField) {
            maxLength = 30;
        } else if (isCityField) {
            maxLength = 100;
        }

        // For finance controls, use number mask with thousands separator
        if (isFinanceControl) {
            return (
                <IMaskInput
                    mask={Number}
                    // Pass mask options as an object
                    // See https://imask.js.org/guide.html#number-mask
                    // IMaskInput will forward these to IMask.MaskedNumber
                    // @ts-ignore
                    maskOptions={{
                        thousandsSeparator: ',',
                        scale: 0, // No decimal places for currency
                        signed: false, // No negative values
                        padFractionalZeros: false,
                        normalizeZeros: true,
                    }}
                    value={formValues[controlKey] || ''}
                    unmask={true} // Get the raw number value
                    onAccept={(value: string, maskRef: any) => {
                        // Check length limit for name fields
                        if (isNameField && maskRef.unmaskedValue && maskRef.unmaskedValue.length > 50) {
                            return; // Don't update if exceeds 50 characters
                        }
                        // Check length limit for address fields
                        if (isAddressField && maskRef.unmaskedValue && maskRef.unmaskedValue.length > 100) {
                            return; // Don't update if exceeds 100 characters
                        }
                        // Check length limit for apartment fields
                        if (isApartmentField && maskRef.unmaskedValue && maskRef.unmaskedValue.length > 30) {
                            return; // Don't update if exceeds 30 characters
                        }
                        // Check length limit for city fields
                        if (isCityField && maskRef.unmaskedValue && maskRef.unmaskedValue.length > 100) {
                            return; // Don't update if exceeds 100 characters
                        }
                        // Store the raw number value (without $ or commas)
                        onChange(controlKey, maskRef.unmaskedValue);
                    }}
                    disabled={isInputDisabled}
                    placeholder={placeholder}
                    id={controlKey}
                    name={ctrl.name}
                    maxLength={maxLength}
                    className={`w-full p-2 border rounded-md ${errorMessage && !isInputDisabled ? 'border-red-500' : 'border border-gray-600'}${isInputDisabled ? ' bg-gray-100' : ''}`}
                    autoComplete="off"
                />
            );
        }

        // Convert mask format if needed (API might send 9s instead of 0s)
        const convertedMask = mask.replaceAll('9', '0').replaceAll('#', '0');

        return (
            <IMaskInput
                mask={convertedMask}
                value={formValues[controlKey] || ''}
                unmask={false} // Value is already unmasked
                onAccept={(value: string, maskRef: any) => {
                    // Check length limit for name fields
                    if (isNameField && maskRef.unmaskedValue && maskRef.unmaskedValue.length > 50) {
                        return; // Don't update if exceeds 50 characters
                    }
                    // Check length limit for address fields
                    if (isAddressField && maskRef.unmaskedValue && maskRef.unmaskedValue.length > 100) {
                        return; // Don't update if exceeds 100 characters
                    }
                    // Check length limit for apartment fields
                    if (isApartmentField && maskRef.unmaskedValue && maskRef.unmaskedValue.length > 30) {
                        return; // Don't update if exceeds 30 characters
                    }
                    // Check length limit for city fields
                    if (isCityField && maskRef.unmaskedValue && maskRef.unmaskedValue.length > 100) {
                        return; // Don't update if exceeds 100 characters
                    }
                    // Store the unmasked value for API submission
                    onChange(controlKey, maskRef.unmaskedValue);
                }}
                disabled={isInputDisabled}
                placeholder={placeholder}
                id={controlKey}
                name={ctrl.name}
                maxLength={maxLength}
                className={`w-full p-2 border rounded-md ${errorMessage && !isInputDisabled ? 'border-red-500' : 'border border-gray-600'}${isInputDisabled ? ' bg-gray-100' : ''}`}
                autoComplete="off"
            />
        );
    };

    // Helper: generateRangeOptions
    const generateRangeOptions = (min: any, max: any, step: any): string[] => {
        const start = Number.parseInt(String(min), 10);
        const end = Number.parseInt(String(max), 10);
        const rangeStep = Number.parseInt(String(step), 10);
        if (Number.isNaN(start) || Number.isNaN(end) || start > end) return [];
        const validatedStep = (rangeStep > 0) ? rangeStep : 1;
        const options: string[] = [];
        for (let i = start; i <= end; i += validatedStep) {
            options.push(String(i));
        }
        return options;
    };
    // Helper variables

    const renderDropdown = (options: string[], placeholder: string) => {
        const isDropdownDisabled = effectiveDisabled;
        // Detect currency fields by dataType from API or Parent/Student Finances entities
        // Hide $ if possibleValues contains string options (like "Own home,Rent,Live with others")
        const hasStringOpts = hasStringOptions(ctrl.possibleValues);
        const isCurrencyField = !hasStringOpts && (ctrl.dataType === 'Currency' || entityName === ENTITY_NAMES.PARENT_FINANCES || entityName === ENTITY_NAMES.STUDENT_FINANCES);
        // Always store/display raw value in formValues, but show $ in label for finance controls
        const value = formValues[controlKey] ?? '';

        // Check if this is the Family Information SAI dropdown
        const isFamilyInfoSAIDropdown = ctrl.name === 'Student Aid Preference';

        // Get the selected option text for tooltip
        const selectedOption = value ? options.find(opt => {
            const normalized = opt.startsWith('$') ? opt.slice(1) : opt;
            const rawOpt = normalized.replaceAll(',', '');
            return rawOpt === value;
        }) : null;

        return (
            <div className="relative">
                <select
                    id={controlKey}
                    name={ctrl.name}
                    value={value}
                    onMouseEnter={() => isFamilyInfoSAIDropdown && setShowSelectTooltip(true)}
                    onMouseLeave={() => setShowSelectTooltip(false)}
                    onFocus={() => isFamilyInfoSAIDropdown && setShowSelectTooltip(true)}
                    onBlur={() => setShowSelectTooltip(false)}
                    onChange={(e) => {
                        let rawValue = e.target.value;
                        // Remove $ prefix and commas if present (shouldn't be, but for safety)
                        if (isCurrencyField && rawValue.startsWith('$')) {
                            rawValue = rawValue.slice(1);
                        }
                        rawValue = rawValue.replaceAll(',', '');
                        // Strip trailing + — it is display-only and must not be stored or sent in API payloads
                        if (rawValue.endsWith('+')) {
                            rawValue = rawValue.slice(0, -1);
                        }
                        onChange(controlKey, rawValue);
                    }}
                    className={`w-full p-2 border rounded-md ${errorMessage && isEnabled ? 'border-red-500' : 'border border-gray-600'}${isDropdownDisabled ? ' bg-white md:bg-gray-100' : ' bg-white'}`}
                    disabled={isDropdownDisabled}
                    style={isFamilyInfoSAIDropdown ? {
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        backgroundColor: isDropdownDisabled ? '#f3f4f6' : '#ffffff',
                        color: value ? '#1f2937' : '#666666',
                        WebkitAppearance: 'none',
                        MozAppearance: 'none',
                        appearance: 'none',
                    } : {
                        backgroundColor: isDropdownDisabled ? '#f3f4f6' : '#ffffff',
                        color: value ? '#1f2937' : '#666666',
                        WebkitAppearance: 'none',
                        MozAppearance: 'none',
                        appearance: 'none',
                    }}
                >
                    <option value="" style={{ backgroundColor: '#ffffff', color: '#666666' }}>{placeholder}</option>
                    {options.map((option: string) => {
                        // Normalize option and compute stored value separately from display label
                        const normalized = option.startsWith('$') ? option.slice(1) : option;
                        const rawOption = normalized.replaceAll(',', '');

                        // Strip + from stored value — + is display-only and must not be sent in API payloads
                        const hasPlus = rawOption.endsWith('+');
                        const storedValue = hasPlus ? rawOption.slice(0, -1) : rawOption;

                        // Currency display handling
                        const numericValue = storedValue;
                        const plusSuffix = hasPlus ? '+' : '';
                        const displayOption = isCurrencyField && storedValue !== ''
                            ? `$${Number(numericValue).toLocaleString()}${plusSuffix}`
                            : rawOption;

                        const { truncated, isTruncated } = truncateText(displayOption, 90);

                        return (
                            <option
                                key={option}
                                value={storedValue}
                                title={isTruncated ? displayOption : undefined}
                                aria-label={displayOption}
                                style={{ backgroundColor: '#ffffff', color: '#1f2937' }}
                            >
                                {truncated}
                            </option>
                        );
                    })}
                </select>

                {/* Tooltip for Family Information SAI dropdown */}
                {isFamilyInfoSAIDropdown && showSelectTooltip && selectedOption && (
                    <div
                        role="tooltip"
                        className="absolute z-50 mt-2 p-3 bg-white text-gray-900 border border-gray-300 rounded-md shadow-lg"
                        style={{
                            top: '100%',
                            left: 0,
                            minWidth: '200px',
                            maxWidth: '520px',
                            fontSize: '13px',
                            lineHeight: '1.4',
                            wordBreak: 'break-word',
                            whiteSpace: 'normal'
                        }}
                    >
                        {selectedOption}
                    </div>
                )}
            </div>
        );
    };

    // Helper: renderGpaOrTestScore
    const renderGpaOrTestScore = () => (
        <div className="mb-4">
            {renderLabelWithTooltip(ctrl._id, labelText, effectiveIsRequired, ctrl.toolTip)}
            {renderTextInput('number', placeholderText, inputDisabled)}
            {errorMessage && <p className="text-red-500 text-sm mt-1">{errorMessage}</p>}
            <DescriptionText text={ctrl.description} />
        </div>
    );

    // Helper: renderStringMultivalued
    const renderStringMultivalued = () => {
        let optionsToRender: string[] = [];

        // Loading State for other dynamic fields (like State)
        if (ctrl.possibleValuesSource && isLoadingOptions && !disabled) {
            return (
                <div className="mb-4">
                    {renderLabelWithTooltip(ctrl._id, labelText, effectiveIsRequired, ctrl.toolTip)}
                    <p className="w-full p-2 text-gray-500 border border-gray-600 rounded-md">
                        Loading options for {labelText.replace('*', '').trim()}...
                    </p>
                    <DescriptionText text={ctrl.description} />
                </div>
            );
        }
        // Range Properties
        else if (ctrl.possibleMinValue !== null && ctrl.possibleMaxValue !== null && ctrl.possibleRangeStep !== null) {
            optionsToRender = generateRangeOptions(ctrl.possibleMinValue, ctrl.possibleMaxValue, ctrl.possibleRangeStep);
        }
        // Inline possibleValues (also use as fallback if possibleValuesSource is set but options haven't loaded)
        else if (!ctrl.possibleValuesSource || ctrl.possibleValues) {
            optionsToRender = Array.isArray(ctrl.possibleValues)
                ? ctrl.possibleValues
                : (ctrl.possibleValues ?? '').split(',').map((option: string) => option.trim()).filter((o: string) => o.length > 0);
        }

        // CRITICAL: Always prefer effectiveDynamicOptions (already adjusted in useMemo)
        // This ensures the memoized, entity-aware adjustments are used
        let finalOptions: any[] = [];

        if (Array.isArray(effectiveDynamicOptions)) {
            finalOptions = effectiveDynamicOptions;
        } else if (Array.isArray(dynamicOptions)) {
            finalOptions = dynamicOptions;
        } else {
            finalOptions = optionsToRender;
        }

        // Dynamically append "+" to the last option for numeric dropdowns with defined min/max ranges
        // This handles household size controls, income/asset controls, and age controls automatically
        // Check for controls that have numeric ranges (min/max/step) regardless of dataType
        // Exclude specific controls that should not have "+" appended
        const excludedFromPlusSuffix = [
            'What year would you enter HCRC?',
            'Your ACT Score',
            'Your SAT Score'
        ];

        // Check if the last computed option is purely numeric (handles possibleValues-based dropdowns)
        const isLastOptionNumeric = finalOptions.length > 0 && (() => {
            const last = finalOptions[finalOptions.length - 1];
            const lastStr = typeof last === 'string' ? last
                : typeof last === 'number' ? String(last)
                    : (last && typeof last === 'object' ? String(last.label ?? last.value ?? '') : '');
            // Strip currency formatting and trailing + before numeric check
            return /^\d+$/.test(lastStr.replaceAll(',', '').replace(/^\$/, '').replace(/\+$/, ''));
        })();

        const shouldAppendPlusToMaxValue =
            ctrl.isMultivalued &&
            !excludedFromPlusSuffix.includes(ctrl.name) &&
            (
                // Range-based: possibleMinValue / possibleMaxValue / possibleRangeStep are all set
                (
                    ctrl.possibleMinValue !== undefined && ctrl.possibleMinValue !== null &&
                    ctrl.possibleMaxValue !== undefined && ctrl.possibleMaxValue !== null &&
                    ctrl.possibleRangeStep !== undefined && ctrl.possibleRangeStep !== null
                ) ||
                // possibleValues-based: no range step but last option is purely numeric
                // (covers Student Finances, Student Age, Household Size, etc.)
                (
                    (ctrl.possibleRangeStep === null || ctrl.possibleRangeStep === undefined) &&
                    isLastOptionNumeric
                )
            );

        if (shouldAppendPlusToMaxValue && finalOptions.length > 0) {
            const last = finalOptions[finalOptions.length - 1];
            // Check if the string contains digits (handles both pure numbers and currency formatted values)
            const hasDigits = (str: string) => /\d/.test(str);

            if (typeof last === 'string' && hasDigits(last) && !last.endsWith('+')) {
                finalOptions = [...finalOptions.slice(0, -1), `${last}+`];
            } else if (typeof last === 'object' && last?.label && hasDigits(String(last.label)) && !String(last.label).endsWith('+')) {
                finalOptions = [...finalOptions.slice(0, -1), { ...last, label: `${last.label}+` }];
            }
        }

        // Apply entity-aware currency replacements to ALL options
        // This catches any options that weren't adjusted in effectiveDynamicOptions
        finalOptions = applyControlMaxReplacement(finalOptions, controlDisplayName, entityName);
        // Normalize finalOptions to string array for renderDropdown
        const normalizedOptions: string[] = finalOptions.map((opt: any) => {
            if (typeof opt === 'string') return opt;
            if (opt && typeof opt === 'object') {
                return opt.label ?? opt.value ?? String(opt);
            }
            return String(opt);
        });

        const placeholder = 'Select';

        return (
            <div className="mb-4">
                {renderLabelWithTooltip(ctrl._id, labelText, effectiveIsRequired, ctrl.toolTip)}
                {normalizedOptions.length > 0 ? (
                    renderDropdown(normalizedOptions, placeholder)
                ) : (
                    <p className="w-full p-2 text-gray-500 border border-gray-600 rounded-md">
                        No options available.
                    </p>
                )}
                {errorMessage && isEnabled && <p className="text-red-500 text-sm mt-1">This field is required</p>}
                <DescriptionText text={ctrl.description} />
                <DynamicDescriptionRenderer
                    fieldName={ctrl.name}
                    currentValue={currentControlValue}
                    allAttributes={allEntityAttributes.all}
                />
            </div>
        );
    };

    // Helper: renderFallbackInput
    const renderFallbackInput = () => {
        const determineMask = () => {
            let maskToUse = ctrl.mask;
            if (maskToUse === 'true') {
                // Check for phone field specifically
                if (ctrl.name.toLowerCase().includes(FIELD_NAMES.PHONE)) {
                    return MASK_PATTERNS.PHONE;
                } else {
                    // Check other masked fields dynamically
                    const otherMaskedFields = MASKED_FIELD_NAMES.filter((field: string) => field !== FIELD_NAMES.PHONE);
                    const matchingField = otherMaskedFields.find((field: string) =>
                        ctrl.name.toLowerCase().includes(field)
                    );

                    if (matchingField === FIELD_NAMES.POSTAL) {
                        return MASK_PATTERNS.POSTAL;
                    } else if (matchingField === FIELD_NAMES.ZIP) {
                        return MASK_PATTERNS.ZIP;
                    }
                }
            }
            return maskToUse;
        };

        const getInputComponent = () => {



            // Check if this field should be masked
            const shouldMask = ctrl.dataType === 'String' && (
                (ctrl.mask) ||
                MASKED_FIELD_NAMES.some((field: string) => ctrl.name.toLowerCase().includes(field))
            );

            if (shouldMask) {
                const maskToUse = determineMask();
                // Only render if we have a valid mask string that is not 'False'
                if (maskToUse && maskToUse !== 'false') {
                    return renderMaskedTextInput(maskToUse, placeholderText);
                }
            }

            let inputType = (ctrl.dataType === 'Number' || ctrl.dataType === 'Real') ? 'number' : 'text';

            return renderTextInput(inputType, placeholderText, inputDisabled);
        };

        return (
            <div className="mb-4">
                {renderLabelWithTooltip(ctrl._id, labelText, effectiveIsRequired, ctrl.toolTip)}
                {entityName !== ENTITY_NAMES.SUMMARY && getInputComponent()}
                {errorMessage && <p className="text-red-500 text-sm mt-1">{errorMessage}</p>}
                <DescriptionText text={ctrl.description} />
            </div>
        );
    };

    // --- PRIMARY RENDERING LOGIC ---

    // Debug logging for financial aid opt-out (must be at top level, not conditional)
    React.useEffect(() => {
        if (entityName === ENTITY_NAMES.SUMMARY) {
            console.groupEnd();
        }
    }, [entityName, ctrl.name, shouldSkipFinances, skipFinancesReason, currentIndependentStatus, parentHasData, studentHasData, financesDisabledFromEdit, parentDisabledFromEdit, allEntityAttributes.family, formValues]);

    // Check if field should be hidden based on enablingFormula
    const shouldHideField = ctrl.enablingFormula && ctrl.enablingAttribute && !isEnabled;

    // Hide fields that don't meet their enablingFormula conditions
    // This ensures fields are hidden initially and only shown when conditions are met
    if (shouldHideField) {
        return null;
    }

    // Special rendering for Summary entity - extract data dynamically from API response
    if (entityName === ENTITY_NAMES.SUMMARY) {
        // Collect Summary entity attributes
        const summaryAttrs = allEntityAttributes.summary ?? tabAttributes[ENTITY_NAMES.SUMMARY] ?? [];

        // Sort by order to ensure consistent processing
        const sortedSummaryAttrs = [...summaryAttrs].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

        // Use the first Summary attribute to trigger rendering (avoid duplication)
        const isFirstSummaryControl = sortedSummaryAttrs.length > 0 && ctrl._id === sortedSummaryAttrs[0]._id;

        // Only render the summary header/accordion once for the first attribute
        if (isFirstSummaryControl) {
            // Extract all derived attributes from API response
            const allDerivedAttributes = derivedAttributesData?.objects?.flatMap((obj: any) => obj.attributes) ?? [];

            // Extract and store Need-Based Grant value in localStorage
            extractAndStoreNeedBasedGrant(allDerivedAttributes);

            // Get conditional description if Need-Based Grant value > 0
            const needBasedGrantValue = parseFloat(localStorage.getItem('needBasedGrantEstimate') || '0');

            // Extract disclaimer from attribute with empty name and long description
            const disclaimerAttr = sortedSummaryAttrs.find(attr =>
                (!attr.name || attr.name.trim() === '') &&
                attr.description &&
                attr.description.length > 100
            );

            // Extract "Connect With Us" URL from constantValue or defaultValue
            const connectAttr = sortedSummaryAttrs.find(attr =>
                attr.constantValue?.startsWith('http') ||
                attr.defaultValue?.startsWith('http')
            );

            const disclaimerTextFromAttr = disclaimerAttr?.description || '';
            const connectUrl = connectAttr?.constantValue || connectAttr?.defaultValue || '';

            // Extract title from attribute with "Connect With Us" name or similar
            const connectTitleAttr = sortedSummaryAttrs.find(attr =>
                attr.name?.toLowerCase().includes('connect')
            );
            const connectButtonText = connectTitleAttr?.name || 'Connect With Us';

            // Toggle handler
            const toggleSection = (sectionKey: string) => {
                setOpenSections(prev => ({
                    ...prev,
                    [sectionKey]: !prev[sectionKey]
                }));
            };

            // Accordion focus/mousedown listeners were moved to the top-level
            // useEffect above to satisfy React Hooks ordering rules.

            // Handle Edit button clicks from Summary accordion
            const handleEditFromSummary = async (sectionKey: string) => {
                setIsEditingFromSummary(true);
                // Don't reset flags - preserve current disabled state
                // Flags will update via onChange when user makes changes

                // Call calculateDerivedAttributesForEntity before navigating
                if (calculateDerivedAttributesForEntity && estimateId) {
                    try {
                        await calculateDerivedAttributesForEntity(estimateId);
                    } catch (error) {
                        console.error('[EDIT FROM SUMMARY] Error calculating derived attributes:', error);
                        // Continue with navigation even if API call fails
                    }
                }

                if (onNavigateToSection) {
                    onNavigateToSection(sectionKey);
                }

                // Scroll to top for better UX
                try {
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                } catch (error) {
                    console.warn('Smooth scroll not supported:', error);
                }
            };

            // Interactive accordion items with keys and content
            // Helper function to get parent finances disabled state
            const getParentFinancesDisabled = () => {
                if (currentIndependentStatus === 'no') {
                    // When student is dependent, Parent Finances is ALWAYS enabled
                    // regardless of SAI/skip options or existing data
                    return false;
                }
                if (currentIndependentStatus === 'yes') {
                    // When student is independent, Parent Finances is ALWAYS disabled
                    return true;
                }
                // Independent status not set: check other conditions
                return parentDisabledFromEdit ||
                    financesDisabledFromEdit ||
                    (shouldSkipFinances && !parentHasData);
            };

            // Helper function to get student finances disabled state
            const getStudentFinancesDisabled = () => {
                if (currentIndependentStatus === 'no') {
                    // When student is dependent, Student Finances is ALWAYS enabled
                    // regardless of SAI/skip options or existing data
                    return false;
                }
                if (currentIndependentStatus === 'yes') {
                    // When student is independent, Student Finances is ALWAYS enabled
                    return false;
                }
                // Independent status not set: check other conditions
                return financesDisabledFromEdit || (shouldSkipFinances && !studentHasData);
            };

            const parentDisabled = getParentFinancesDisabled();
            const studentDisabled = getStudentFinancesDisabled();

            // Helper function to get parent title - no asterisk when enabled
            const getParentTitle = () => {
                return parentDisabled ? 'Parent Finances *' : 'Parent Finances';
            };

            // Helper function to get student title - no asterisk when enabled
            const getStudentTitle = () => {
                return studentDisabled ? 'Student Finances *' : 'Student Finances';
            };

            const accordionItems = [
                {
                    key: 'student',
                    title: 'Student Information',
                    disabled: false,
                    renderContent: () => (
                        <div className="py-2 pr-4 pl-0">
                            <div className="flex justify-end items-center ">
                                <button
                                    type="button"
                                    onClick={() => handleEditFromSummary('student')}
                                    className=" py-1 font-semibold text-base leading-6 underline text-[#095CC9] tracking-[0.15px]"
                                    style={{

                                        textDecorationStyle: 'solid',
                                        textDecorationThickness: '0%',
                                        textDecorationSkipInk: 'auto',
                                    }}
                                    aria-label="Edit student information"
                                >
                                    Edit
                                </button>
                            </div>
                            <dl className="grid grid-cols-1 gap-4">
                                {/* Dynamically render all student info fields */}
                                {(() => {
                                    const studentInfoAttrs = allEntityAttributes.studentInfo || [];

                                    // Filter out informational attributes from summary display
                                    return studentInfoAttrs
                                        .filter((attr) => !attr.isInformational)
                                        .sort((a, b) => (a.order || 0) - (b.order || 0))
                                        .map((attr) => {
                                            let value = formValues[attr._id] || attr.value || attr.displayValue;

                                            // Filter out "false" default values
                                            if (value === "false" || value === false) {
                                                value = '';
                                            }

                                            // Re-append + for display when value is the max of a range or possibleValues dropdown
                                            // (+ is stripped from stored values to keep API payloads clean)
                                            const siExcluded = ['What year would you enter HCRC?', 'Your ACT Score', 'Your SAT Score'];
                                            if (attr.isMultivalued && !siExcluded.includes(attr.name) && value) {
                                                // Normalize stored value to raw numeric for comparison (handles "$500,000", "500,000", "500000")
                                                const siNumericValue = String(value).replace(/^\$/, '').replaceAll(',', '').replace(/\+$/, '').trim();
                                                // Range-based
                                                if (attr.possibleMinValue != null && attr.possibleMaxValue != null && attr.possibleRangeStep != null
                                                    && /^\d+$/.test(siNumericValue) && Number(siNumericValue) === Number(attr.possibleMaxValue)) {
                                                    value = `${siNumericValue}+`;
                                                    // possibleValues-based: find max numeric value and compare
                                                } else if (attr.possibleValues != null && attr.possibleValues !== '') {
                                                    const siOpts = typeof attr.possibleValues === 'string'
                                                        ? attr.possibleValues.split(',').map((o: string) => o.trim()).filter(Boolean)
                                                        : Array.isArray(attr.possibleValues) ? attr.possibleValues : [];
                                                    const siNumericOpts = siOpts.map((o: any) => {
                                                        const raw = typeof o === 'string' ? o.trim()
                                                            : typeof o === 'number' ? String(o)
                                                                : (o && typeof o === 'object' ? String(o.value ?? o.displayValue ?? o.label ?? '') : '');
                                                        const str = raw.replace(/\+$/, '');
                                                        return /^\d+$/.test(str) ? Number(str) : null;
                                                    }).filter((n: any): n is number => n !== null);
                                                    const siMax = siNumericOpts.length > 0 ? Math.max(...siNumericOpts) : null;
                                                    if (siMax !== null && /^\d+$/.test(siNumericValue) && Number(siNumericValue) === siMax) {
                                                        value = `${siNumericValue}+`;
                                                    }
                                                }
                                            }

                                            // Format boolean values
                                            if (attr.dataType === 'Boolean') {
                                                value = formatBooleanForDisplay(value);
                                            }
                                            // Format date values
                                            else if (attr.dataType === 'Date' && value) {
                                                try {
                                                    const date = new Date(value);
                                                    if (!isNaN(date.getTime())) {
                                                        value = date.toLocaleDateString('en-US', {
                                                            year: 'numeric',
                                                            month: 'long',
                                                            day: 'numeric'
                                                        });
                                                    }
                                                } catch (e) {
                                                    console.warn('Date parsing failed, keeping original value:', e);
                                                }
                                            }
                                            // Use dropdown display value if available (for both static and dynamic options)
                                            else if (attr.isMultivalued || attr.possibleValuesSource) {
                                                value = getDropdownDisplayValueForControl(
                                                    value,
                                                    studentInfoAttrs,
                                                    attr.name,
                                                    [attr.name]
                                                );
                                            }

                                            // Display empty string for null/undefined values
                                            if (value === null || value === undefined) {
                                                value = '';
                                            }

                                            return (
                                                <div key={attr._id} className="grid grid-cols-[3fr_2fr] gap-x-8 items-start">
                                                    <dt className="text-sm font-regular text-black">
                                                        {attr.name}:
                                                    </dt>
                                                    <dd className="w-full text-sm text-gray-900 text-left font-semibold">
                                                        {value || 'N/A'}
                                                    </dd>
                                                </div>
                                            );
                                        });
                                })()}
                            </dl>

                            <div className="pt-4 border-gray-200">
                                <dl className="grid grid-cols-1 gap-3">
                                    <div className="grid grid-cols-[3fr_2fr] gap-x-8 items-start">
                                        <dt className="text-sm font-regular text-black">Contact Details:</dt>
                                        <dd className="w-full text-sm text-gray-900 text-left font-semibold">
                                            {(() => {
                                                const cd = studentInfoData.contactDetails || {};
                                                const lines: string[] = [];
                                                const fullName = [cd.name, cd.lastName].filter(Boolean).join(' ').trim();
                                                if (fullName && fullName !== '') lines.push(fullName);
                                                if (cd.address && cd.address !== '') lines.push(cd.address);
                                                if (cd.apartment && cd.apartment !== '') lines.push(cd.apartment);
                                                const cityStateZipParts: string[] = [];
                                                if (cd.city && cd.city !== '') cityStateZipParts.push(cd.city);
                                                if (cd.state && cd.state !== '') cityStateZipParts.push(cd.state);
                                                const zip = (cd.zipCode && cd.zipCode !== '') ? cd.zipCode : '';
                                                const zipSuffix = zip ? (cityStateZipParts.length > 0 ? ` ${zip}` : zip) : '';
                                                const cityStateZip = cityStateZipParts.join(', ') + zipSuffix;
                                                if (cityStateZip.trim()) lines.push(cityStateZip.trim());
                                                if (cd.phone && cd.phone !== '') lines.push(cd.phone);
                                                if (cd.email && cd.email !== '') lines.push(cd.email);
                                                if (lines.length === 0) lines.push('N/A');

                                                return (
                                                    <div className="space-y-1 leading-6 whitespace-pre-line">
                                                        {lines.map((line) => (
                                                            <div key={line}>{line}</div>
                                                        ))}
                                                    </div>
                                                );
                                            })()}
                                        </dd>
                                    </div>
                                </dl>
                            </div>
                        </div>
                    )
                },
                {
                    key: 'academic',
                    title: 'Academic Scores',
                    renderContent: () => (
                        <div className="py-4 pr-4 pl-0 ">
                            <div className="flex justify-end items-center ">
                                <button
                                    type="button"
                                    onClick={() => handleEditFromSummary('academic')}
                                    className=" py-1 font-semibold text-base leading-6 underline text-[#095CC9] tracking-[0.15px]"
                                    style={{

                                        textDecorationStyle: 'solid',
                                        textDecorationThickness: '0%',
                                        textDecorationSkipInk: 'auto',
                                    }}
                                    aria-label="Edit academic scores"
                                >
                                    Edit
                                </button>
                            </div>
                            <dl className="grid grid-cols-1 gap-4">
                                {/* Dynamically render all academic scores fields */}
                                {(() => {
                                    const academicAttrs = allEntityAttributes.academic ?? [];

                                    // Filter out informational attributes from summary display
                                    return academicAttrs
                                        .filter((attr) => !attr.isInformational)
                                        .filter((attr) => shouldShowConditionalField(attr, allEntityAttributes, formValues))
                                        .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
                                        .map((attr) => {
                                            let value = formValues[attr._id] ?? attr.value ?? attr.displayValue;

                                            // Filter out "false" default values
                                            if (value === "false") {
                                                value = '';
                                            }

                                            // Format boolean values
                                            if (attr.dataType === 'Boolean') {
                                                value = formatBooleanForDisplay(value);
                                            }
                                            // Format date values
                                            else if (attr.dataType === 'Date' && value) {
                                                try {
                                                    const date = new Date(value);
                                                    if (!isNaN(date.getTime())) {
                                                        value = date.toLocaleDateString('en-US', {
                                                            year: 'numeric',
                                                            month: 'long',
                                                            day: 'numeric'
                                                        });
                                                    }
                                                } catch (e) {
                                                    console.warn('Date parsing failed, keeping original value:', e);
                                                }
                                            }
                                            // Use dropdown display value if available
                                            else if (attr.isMultivalued && attr.possibleValues) {
                                                value = getDropdownDisplayValueForControl(
                                                    value,
                                                    academicAttrs,
                                                    attr.name,
                                                    [attr.name]
                                                );
                                            }

                                            // Display empty string for null/undefined values
                                            if (value === null || value === undefined) {
                                                value = '';
                                            }

                                            return (
                                                <div key={attr._id} className="grid grid-cols-[3fr_2fr] gap-x-8 items-start">
                                                    <dt className="text-sm font-regular text-black">
                                                        {attr.name === 'Your Student Aid Index' ? 'Student Aid preference' : attr.name}:
                                                    </dt>
                                                    <dd className="w-full text-sm text-gray-900 text-left font-semibold">
                                                        {value || 'N/A'}
                                                    </dd>
                                                </div>
                                            );
                                        });
                                })()}
                            </dl>
                        </div>
                    )
                },
                {
                    key: 'family',
                    title: 'Family Information',
                    renderContent: () => (
                        <div className="py-4 pr-4 pl-0">
                            <div className="flex justify-end items-center ">
                                <button
                                    type="button"
                                    onClick={() => handleEditFromSummary('family')}
                                    className=" py-1 font-semibold text-base leading-6 underline text-[#095CC9] tracking-[0.15px]"
                                    style={{

                                        textDecorationStyle: 'solid',
                                        textDecorationThickness: '0%',
                                        textDecorationSkipInk: 'auto',
                                    }}
                                    aria-label="Edit family information"
                                >
                                    Edit
                                </button>
                            </div>
                            <dl className="grid grid-cols-1 gap-4">
                                {/* Show message when finances are skipped and student is not independent */}
                                {shouldSkipFinances && skipFinancesReason === 'opted-out' && familyInfoData?.independentStudent !== 'Yes' && familyInfoData?.independentStudent !== 'No' ? (
                                    <p className="text-sm text-gray-500 mb-4">
                                        * Not applicable because Student opted out OR is an international citizen ineligible for need-based financial aid
                                    </p>
                                ) : (
                                    (() => {
                                        const familyAttrs = allEntityAttributes.family || [];

                                        // Filter out informational attributes from summary display
                                        return familyAttrs
                                            .filter((attr) => !attr.isInformational)
                                            .filter((attr) => {
                                                // Hide "Your Student Aid Index" field if it has no value to avoid showing "N/A"
                                                if (attr.name === 'Your Student Aid Index') {
                                                    const value = formValues[attr._id] ?? attr.value ?? attr.displayValue;
                                                    const hasValue = value && value !== '' && value !== 'false';

                                                    // Hide if no value (whether user opted out or hasn't entered SAI)
                                                    if (!hasValue) {
                                                        return false;
                                                    }
                                                }
                                                return true;
                                            })
                                            .sort((a, b) => (a.order || 0) - (b.order || 0))
                                            .map((attr) => {
                                                let value = formValues[attr._id] || attr.value || attr.displayValue;

                                                // Filter out "false" default values
                                                if (value === "false" || value === false) {
                                                    value = '';
                                                }

                                                // Format boolean values
                                                if (attr.dataType === 'Boolean') {
                                                    value = formatBooleanForDisplay(value);
                                                }
                                                // Format date values
                                                else if (attr.dataType === 'Date' && value) {
                                                    try {
                                                        const date = new Date(value);
                                                        if (!isNaN(date.getTime())) {
                                                            value = date.toLocaleDateString('en-US', {
                                                                year: 'numeric',
                                                                month: 'long',
                                                                day: 'numeric'
                                                            });
                                                        }
                                                    } catch (e) {
                                                        console.warn('Date parsing failed, keeping original value:', e);
                                                    }
                                                }
                                                // Use dropdown display value if available
                                                else if (attr.isMultivalued && attr.possibleValues) {
                                                    value = getDropdownDisplayValueForControl(
                                                        value,
                                                        familyAttrs,
                                                        attr.name,
                                                        [attr.name]
                                                    );
                                                }

                                                // Display empty string for null/undefined values
                                                if (value === null || value === undefined) {
                                                    value = '';
                                                }

                                                return (
                                                    <div key={attr._id} className="grid grid-cols-[3fr_2fr] gap-x-8 items-start">
                                                        <dt className="text-sm font-regular text-black">
                                                            {attr.name === 'Your Student Aid Index' ? 'Student Aid preference' : attr.name}:
                                                        </dt>
                                                        <dd className="w-full text-sm text-gray-900 text-left font-semibold">
                                                            {value || 'N/A'}
                                                        </dd>
                                                    </div>
                                                );
                                            });
                                    })()
                                )}
                            </dl>
                        </div >
                    )
                },
                // Parent Finances: ALWAYS enabled when Independent='No'; disabled when Independent='Yes' OR SAI provided
                {
                    key: 'parent',
                    title: getParentTitle(),
                    disabled: parentDisabled,
                    renderContent: () => (
                        <div className="py-2 pr-4 pl-0 ">
                            {parentDisabled && (shouldSkipFinances && skipFinancesReason === 'opted-out') ? (
                                <output
                                    className="p-6 bg-gray-50 border border-gray-200 rounded-md text-center block"
                                    aria-label="Parent finances section is disabled"
                                >
                                    <svg
                                        className="mx-auto h-12 w-12 text-gray-400 mb-3"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                        stroke="currentColor"
                                        aria-hidden="true"
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                                        />
                                    </svg>
                                    <p className="text-sm font-medium text-gray-900 mb-2">
                                        Parent Finances Not Required
                                    </p>
                                    <p className="text-xs text-gray-600 mb-3">
                                        This section is disabled because you opted out of need-based financial aid calculation or indicated you are an international citizen ineligible for financial aid.
                                    </p>
                                    <button
                                        type="button"
                                        onClick={() => {
                                            if (onNavigateToSection) {
                                                onNavigateToSection('family');
                                            }
                                        }}
                                        className="text-xs text-blue-600 hover:text-blue-800 underline focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 rounded px-2 py-1"
                                        aria-label="Go to Family Information to enable financial aid calculation"
                                    >
                                        Change in Family Information ?
                                    </button>
                                </output>
                            ) : (
                                <>
                                    <div className="flex justify-end items-center ">
                                        <button
                                            type="button"
                                            onClick={() => handleEditFromSummary('parent')}
                                            disabled={parentDisabled}
                                            className=" py-1 font-semibold text-base leading-6 underline text-[#095CC9] tracking-[0.15px]"
                                            style={{

                                                textDecorationStyle: 'solid',
                                                textDecorationThickness: '0%',
                                                textDecorationSkipInk: 'auto',
                                            }}
                                            aria-label="Edit parent finances"
                                            aria-disabled={parentDisabled}
                                        >
                                            Edit
                                        </button>
                                    </div>
                                    <dl className="grid grid-cols-1 gap-4">
                                        {/* Dynamically render all parent finances fields */}
                                        {(() => {
                                            const parentFinAttrs = allEntityAttributes.parentFinances || [];

                                            // Filter out informational attributes from summary display
                                            return parentFinAttrs
                                                .filter((attr) => !attr.isInformational)
                                                .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
                                                .map((attr) => {
                                                    let value = formValues[attr._id] ?? attr.value ?? attr.displayValue;

                                                    // Filter out "false" default values
                                                    if (value === "false") {
                                                        value = '';
                                                    }

                                                    // Re-append + for display when value is the max of a range or possibleValues dropdown
                                                    // (+ is stripped from stored values to keep API payloads clean)
                                                    const pfExcluded = ['What year would you enter HCRC?', 'Your ACT Score', 'Your SAT Score'];
                                                    if (attr.isMultivalued && !pfExcluded.includes(attr.name) && value) {
                                                        // Normalize stored value to raw numeric for comparison (handles "$500,000", "500,000", "500000")
                                                        const pfNumericValue = String(value).replace(/^\$/, '').replaceAll(',', '').replace(/\+$/, '').trim();
                                                        // Range-based
                                                        if (attr.possibleMinValue != null && attr.possibleMaxValue != null && attr.possibleRangeStep != null
                                                            && /^\d+$/.test(pfNumericValue) && Number(pfNumericValue) === Number(attr.possibleMaxValue)) {
                                                            value = `${pfNumericValue}+`;
                                                            // possibleValues-based: find max numeric value and compare
                                                        } else if (attr.possibleValues != null && attr.possibleValues !== '') {
                                                            const pfOpts = typeof attr.possibleValues === 'string'
                                                                ? attr.possibleValues.split(',').map((o: string) => o.trim()).filter(Boolean)
                                                                : Array.isArray(attr.possibleValues) ? attr.possibleValues : [];
                                                            const pfNumericOpts = pfOpts.map((o: any) => {
                                                                const raw = typeof o === 'string' ? o.trim()
                                                                    : typeof o === 'number' ? String(o)
                                                                        : (o && typeof o === 'object' ? String(o.value ?? o.displayValue ?? o.label ?? '') : '');
                                                                const str = raw.replace(/\+$/, '');
                                                                return /^\d+$/.test(str) ? Number(str) : null;
                                                            }).filter((n: any): n is number => n !== null);
                                                            const pfMax = pfNumericOpts.length > 0 ? Math.max(...pfNumericOpts) : null;
                                                            if (pfMax !== null && /^\d+$/.test(pfNumericValue) && Number(pfNumericValue) === pfMax) {
                                                                value = `${pfNumericValue}+`;
                                                            }
                                                        }
                                                    }

                                                    // Format boolean values
                                                    if (attr.dataType === 'Boolean') {
                                                        value = formatBooleanForDisplay(value);
                                                    }
                                                    // Format currency values for financial fields
                                                    else if (value && isFinancialControl(attr.name, attr)) {
                                                        value = formatCurrency(value);
                                                    }
                                                    // Format date values
                                                    else if (attr.dataType === 'Date' && value) {
                                                        try {
                                                            const date = new Date(value);
                                                            if (!isNaN(date.getTime())) {
                                                                value = date.toLocaleDateString('en-US', {
                                                                    year: 'numeric',
                                                                    month: 'long',
                                                                    day: 'numeric'
                                                                });
                                                            }
                                                        } catch (e) {
                                                            console.warn('Date parsing failed, keeping original value:', e);
                                                        }
                                                    }
                                                    // Use dropdown display value if available
                                                    else if (attr.isMultivalued && attr.possibleValues) {
                                                        value = getDropdownDisplayValueForControl(
                                                            value,
                                                            parentFinAttrs,
                                                            attr.name,
                                                            [attr.name]
                                                        );
                                                    }

                                                    // Display empty string for null/undefined values
                                                    if (value === null || value === undefined) {
                                                        value = '';
                                                    }

                                                    return (
                                                        <div key={attr._id} className="grid grid-cols-[3fr_2fr] gap-x-8 items-start">
                                                            <dt className="text-sm font-regular text-black">
                                                                {attr.name}:
                                                            </dt>
                                                            <dd className="w-full text-sm text-gray-900 text-left font-semibold">
                                                                {value || 'N/A'}
                                                            </dd>
                                                        </div>
                                                    );
                                                });
                                        })()}
                                    </dl>
                                </>
                            )}
                        </div>
                    )
                },
                // Student Finances: ALWAYS enabled when Independent='No'; disabled when SAI provided
                {
                    key: 'studentFinances',
                    title: getStudentTitle(),
                    disabled: studentDisabled,
                    renderContent: () => (
                        <div className="py-2 pr-4 pl-0">
                            {studentDisabled && (shouldSkipFinances && skipFinancesReason === 'opted-out') ? (
                                <output
                                    className="p-6 bg-gray-50 border border-gray-200 rounded-md text-center block"
                                    aria-label="Student finances section is disabled"
                                >
                                    <svg
                                        className="mx-auto h-12 w-12 text-gray-400 mb-3"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                        stroke="currentColor"
                                        aria-hidden="true"
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                                        />
                                    </svg>
                                    <p className="text-sm font-medium text-gray-900 mb-2">
                                        Student Finances Not Required
                                    </p>
                                    <p className="text-xs text-gray-600 mb-3">
                                        This section is disabled because you opted out of need-based financial aid calculation or indicated you are an international citizen ineligible for financial aid.
                                    </p>
                                    <button
                                        type="button"
                                        onClick={() => {
                                            if (onNavigateToSection) {
                                                onNavigateToSection('family');
                                            }
                                        }}
                                        className="text-xs text-blue-600 hover:text-blue-800 underline focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 rounded px-2 py-1"
                                        aria-label="Go to Family Information to enable financial aid calculation"
                                    >
                                        Change in Family Information ?
                                    </button>
                                </output>
                            ) : (
                                <>
                                    <div className="flex justify-end items-center ">
                                        <button
                                            type="button"
                                            onClick={() => handleEditFromSummary('studentFinances')}
                                            disabled={studentDisabled}
                                            className=" py-1 font-semibold text-base leading-6 underline text-[#095CC9] tracking-[0.15px]"
                                            style={{

                                                textDecorationStyle: 'solid',
                                                textDecorationThickness: '0%',
                                                textDecorationSkipInk: 'auto'
                                            }}
                                            aria-label="Edit student finances"
                                            aria-disabled={studentDisabled}
                                        >
                                            Edit
                                        </button>
                                    </div>

                                    <dl className="grid grid-cols-1 gap-4">
                                        {/* Dynamically render all student finances fields */}
                                        {(() => {
                                            const studentFinAttrs = allEntityAttributes.studentFinances || [];

                                            // Filter out informational attributes from summary display
                                            return studentFinAttrs
                                                .filter((attr) => !attr.isInformational)
                                                .sort((a, b) => (a.order || 0) - (b.order ?? 0))
                                                .map((attr) => {
                                                    let value = formValues[attr._id] || (attr.value ?? attr.displayValue);

                                                    // Filter out "false" default values
                                                    if (value === "false") {
                                                        value = '';
                                                    }

                                                    // Re-append + for display when value is the max of a range or possibleValues dropdown
                                                    // (+ is stripped from stored values to keep API payloads clean)
                                                    const sfExcluded = ['What year would you enter HCRC?', 'Your ACT Score', 'Your SAT Score'];
                                                    if (attr.isMultivalued && !sfExcluded.includes(attr.name) && value) {
                                                        // Normalize stored value to raw numeric for comparison (handles "$500,000", "500,000", "500000")
                                                        const sfNumericValue = String(value).replace(/^\$/, '').replaceAll(',', '').replace(/\+$/, '').trim();
                                                        // Range-based
                                                        if (attr.possibleMinValue != null && attr.possibleMaxValue != null && attr.possibleRangeStep != null
                                                            && /^\d+$/.test(sfNumericValue) && Number(sfNumericValue) === Number(attr.possibleMaxValue)) {
                                                            value = `${sfNumericValue}+`;
                                                            // possibleValues-based: find max numeric value and compare
                                                        } else if (attr.possibleValues != null && attr.possibleValues !== '') {
                                                            const sfOpts = typeof attr.possibleValues === 'string'
                                                                ? attr.possibleValues.split(',').map((o: string) => o.trim()).filter(Boolean)
                                                                : Array.isArray(attr.possibleValues) ? attr.possibleValues : [];
                                                            const sfNumericOpts = sfOpts.map((o: any) => {
                                                                const raw = typeof o === 'string' ? o.trim()
                                                                    : typeof o === 'number' ? String(o)
                                                                        : (o && typeof o === 'object' ? String(o.value ?? o.displayValue ?? o.label ?? '') : '');
                                                                const str = raw.replace(/\+$/, '');
                                                                return /^\d+$/.test(str) ? Number(str) : null;
                                                            }).filter((n: any): n is number => n !== null);
                                                            const sfMax = sfNumericOpts.length > 0 ? Math.max(...sfNumericOpts) : null;
                                                            if (sfMax !== null && /^\d+$/.test(sfNumericValue) && Number(sfNumericValue) === sfMax) {
                                                                value = `${sfNumericValue}+`;
                                                            }
                                                        }
                                                    }

                                                    // Format boolean values
                                                    if (attr.dataType === 'Boolean') {
                                                        value = formatBooleanForDisplay(value);
                                                    }
                                                    // Format currency values for financial fields
                                                    else if (value && isFinancialControl(attr.name, attr)) {
                                                        value = formatCurrency(value);
                                                    }
                                                    // Format date values
                                                    else if (attr.dataType === 'Date' && value) {
                                                        try {
                                                            const date = new Date(value);
                                                            if (!isNaN(date.getTime())) {
                                                                value = date.toLocaleDateString('en-US', {
                                                                    year: 'numeric',
                                                                    month: 'long',
                                                                    day: 'numeric'
                                                                });
                                                            }
                                                        } catch (e) {
                                                            console.warn('Date parsing failed, keeping original value:', e);
                                                        }
                                                    }
                                                    // Use dropdown display value if available
                                                    else if (attr.isMultivalued && attr.possibleValues) {
                                                        value = getDropdownDisplayValueForControl(
                                                            value,
                                                            studentFinAttrs,
                                                            attr.name,
                                                            [attr.name]
                                                        );
                                                    }

                                                    // Display empty string for null/undefined values
                                                    if (value === null || value === undefined) {
                                                        value = '';
                                                    }

                                                    return (
                                                        <div key={attr._id} className="grid grid-cols-[3fr_2fr] gap-x-8 items-start">
                                                            <dt className="text-sm font-regular text-black">
                                                                {attr.name}:
                                                            </dt>
                                                            <dd className="w-full text-sm text-gray-900 text-left font-semibold">
                                                                {value || 'N/A'}
                                                            </dd>
                                                        </div>
                                                    );
                                                });
                                        })()}
                                    </dl>
                                </>
                            )}
                        </div>
                    )
                }
            ];

            // Format the estimated net price for display
            const displayPrice = estimatedNetPrice ? formatCurrency(estimatedNetPrice) : 'N/A';
            const backgroundColor = tenantConfig?.authority?.authorityBranding?.primaryColor;

            // Extract title and optional note from first Summary attribute
            const titleAttr = sortedSummaryAttrs.find(attr =>
                attr.name && attr.name.trim() !== ''
            );
            const summaryTitle = titleAttr?.name ?? '';

            // Determine preheader text using multi-tenant logic
            const { headerNote } = determinePreheaderText(
                allDerivedAttributes,
                sortedSummaryAttrs,
                derivedAttributesData,
                needBasedGrantValue,
                tenantConfig?.authority?.displayName ?? ''
            );


            return (
                <div className="summary-header">
                    <h1 className="text-3xl font-bold mb-2" style={{ color: backgroundColor }}
                    >
                        {summaryTitle}: {displayPrice}
                    </h1>

                    {/* Conditional preheader text - shows different content based on need-based grant value */}
                    {headerNote && (
                        <p className="text-gray-600 mb-3">
                            <TextWithLinks text={headerNote} />
                        </p>
                    )}

                    {/* Interactive accordion between descriptions */}
                    <section className=" rounded-md mb-3" aria-label="Summary sections">
                        {accordionItems.map((item, index) => {
                            const isOpen = openSections[item.key];
                            const headerId = `accordion-header-${item.key}`;
                            const panelId = `accordion-panel-${item.key}`;
                            const isDisabled = item.disabled === true;

                            return (
                                <div
                                    key={item.key}
                                >
                                    <h3 className="m-0">
                                        <button
                                            type="button"
                                            id={headerId}
                                            aria-expanded={isOpen}
                                            aria-controls={panelId}
                                            disabled={isDisabled}
                                            aria-disabled={isDisabled}
                                            onClick={() => !isDisabled && toggleSection(item.key)}
                                            className={`w-full flex justify-between items-center py-2 pl-0 pr-4 bg-white transition-colors border-b border-black ${isDisabled
                                                ? 'cursor-not-allowed opacity-60 hover:bg-white'
                                                : 'hover:bg-gray-50'
                                                }`}
                                        >
                                            <span className={`font-semibold text-xl text-left ${isDisabled ? 'text-gray-500' : 'text-black'
                                                }`}>
                                                {item.title}
                                            </span>
                                            {isDisabled ? (
                                                <ChevronDownIcon className="w-5 h-5 text-gray-500" />
                                            ) : (
                                                <svg
                                                    className={`w-5 h-5 transform transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
                                                    fill="none"
                                                    stroke="currentColor"
                                                    viewBox="0 0 24 24"
                                                    aria-hidden="true"
                                                    color={backgroundColor}
                                                >
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M19 9l-7 7-7-7" />
                                                </svg>
                                            )}
                                        </button>
                                    </h3>
                                    {isOpen && (
                                        <section
                                            id={panelId}
                                            aria-labelledby={headerId}
                                        >
                                            {item.renderContent()}
                                        </section>
                                    )}
                                </div>
                            );
                        })}
                    </section>
                    {(() => {
                        if (familyInfoData?.independentStudent === 'Yes') {
                            return (
                                <p className="text-sm text-gray-500 mb-6">
                                    *Not applicable because Student has self-reported as independent based on age, marital status, dependents, military service, or other qualifying circumstances.
                                </p>
                            );
                        }
                        if (shouldSkipFinances && skipFinancesReason === 'sai') {
                            return (
                                <p className="text-sm text-gray-500 mb-2">
                                    * Not applicable because Student provided SAI
                                </p>
                            );
                        }
                        if (shouldSkipFinances && skipFinancesReason === 'opted-out' && familyInfoData?.independentStudent !== 'No') {
                            return (
                                <p className="text-sm text-gray-500 mb-2">
                                    * Not applicable because Student opted out OR is an international citizen ineligible for need-based financial aid
                                </p>
                            );
                        }
                        return null;
                    })()}

                    {/* Action Buttons and Disclaimer Text - now inside bordered container */}
                    <div className="flex justify-between items-center mb-2">
                        <button
                            type="button"
                            onClick={handleBack}
                            className="bg-white text-[#AFAFAF] border font-semibold py-2 px-5 rounded hover:border-black hover:text-black hover:underline transition-all duration-200"
                            aria-label="Go back to previous tab"
                            style={{
                                boxShadow: '0 4px 8px rgba(0, 0, 0, 0.4)'
                            }}
                        >
                            Back
                        </button>
                        <button
                            type="button"
                            onClick={() => {
                                if (typeof handleConnectWithUs === 'function') {
                                    handleConnectWithUs();
                                }
                                if (connectUrl) {
                                    window.open(connectUrl, '_blank', 'noopener,noreferrer');
                                } else {
                                    openContactUs();
                                }
                            }}
                            className="px-6 py-2 text-white rounded-md transition-all duration-200"
                            aria-label={connectButtonText}
                            style={{
                                backgroundColor: tenantConfig?.authority?.authorityBranding?.secondaryColor ?? "",
                                color: tenantConfig?.authority?.authorityBranding?.accentColor ?? "",
                                boxShadow: '0 4px 8px rgba(0, 0, 0, 0.6)'
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor = tenantConfig?.authority?.authorityBranding?.primaryColor ?? '';
                                e.currentTarget.style.color = tenantConfig?.authority?.authorityBranding?.accentColor ?? '';
                                e.currentTarget.style.textDecoration = 'underline';
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = tenantConfig?.authority?.authorityBranding?.secondaryColor ?? '';
                                e.currentTarget.style.color = tenantConfig?.authority?.authorityBranding?.accentColor ?? '';
                                e.currentTarget.style.textDecoration = 'none';
                            }}
                        >
                            {connectButtonText}
                        </button>
                    </div>

                    {/* Disclaimer Text */}
                    {(() => {
                        // Use the dynamically extracted disclaimer text from API
                        const finalDisclaimerText = disclaimerTextFromAttr ?? disclaimerText;

                        // Determine footer text using multi-tenant logic
                        const { footerTextToRender, showWorkStudyFooter, workStudyFooterText } = determineFooterText(
                            allDerivedAttributes,
                            sortedSummaryAttrs,
                            derivedAttributesData,
                            finalDisclaimerText,
                            needBasedGrantValue,
                            tenantConfig?.authority?.displayName ?? ''
                        );


                        // Only render if footer text is determined
                        if (!footerTextToRender) {
                            return null;
                        }

                        // Strip only the #* and *# delimiter characters, keeping the enclosed text visible
                        const cleanedFooterText = footerTextToRender.replace(/#\*/g, '').replace(/\*#/g, '').trim();

                        // Split footer text into paragraphs dynamically
                        let parts: string[] = [];

                        if (cleanedFooterText) {
                            // Split by double newlines OR <br> tags (paragraph/line breaks from API data)
                            parts = cleanedFooterText.split(/\n{2,}|<br\s*\/?>/).map((s: string) => s.trim()).filter(Boolean);
                            // If no splits found, treat entire text as single paragraph
                            if (parts.length === 0 && cleanedFooterText) {
                                parts = [cleanedFooterText];
                            }
                        }

                        return (
                            <div className="summary-footer">
                                {/* Loyola tenant: Conditional Work-Study footer text for options 1 and 2 */}
                                {showWorkStudyFooter && workStudyFooterText && (
                                    <div className="mb-4">
                                        <p className="text-[#6B7280] text-xs leading-6 font-normal mb-2">
                                            <TextWithLinks text={workStudyFooterText} />
                                        </p>
                                    </div>
                                )}

                                {/* Footer text (conditional for Rhodes, standard for Loyola) */}
                                {parts.map((p: string, i: number) => (
                                    <p key={p} className={`text-[#6B7280] text-xs leading-6 font-normal ${i < parts.length - 1 ? 'mb-4' : 'mb-0'}`}>
                                        <TextWithLinks text={p} />
                                    </p>
                                ))}
                            </div>
                        );
                    })()}
                </div>
            );
        }

        // For other Summary controls, skip rendering to avoid duplication
        return null;
    }

    // Handle informational attributes - display as read-only text when isInformational is true
    if (ctrl.isInformational === true) {
        // Hide informational messages on the summary page only
        if (entityName === 'Summary') {
            return null;
        }

        // Check if there's an enablingFormula to evaluate
        if (ctrl.enablingFormula) {
            const formula = ctrl.enablingFormula.trim();

            // Evaluate the formula to determine if the message should be shown
            // Example formula: if(do_you_have_any_immediate_family_members_who_have_graduated_from_drury == "Yes" || will_you_receive_dual_credit_through_drury == "Yes", true, false)
            const evaluateFormula = (formulaStr: string): boolean => {
                try {
                    // Extract the condition from the if() statement
                    const ifMatch = /^if\s*\((.*),\s*true\s*,\s*false\s*\)$/i.exec(formulaStr);
                    if (!ifMatch) {
                        console.warn('Invalid enablingFormula format:', formulaStr);
                        return false;
                    }

                    const condition = ifMatch[1].trim();

                    // Split by || (OR operator) to handle multiple conditions
                    const orConditions = condition.split('||').map(c => c.trim());

                    // Evaluate each OR condition
                    for (const orCondition of orConditions) {
                        // Split by && (AND operator) if present
                        const andConditions = orCondition.split('&&').map(c => c.trim());
                        let allAndConditionsTrue = true;

                        for (const andCondition of andConditions) {
                            // Parse the condition: variable == "value"
                            const conditionMatch = (/^([a-zA-Z_]\w*)\s*==\s*"([^"]+)"$/).exec(andCondition);
                            if (!conditionMatch) {
                                console.warn('Invalid condition format:', andCondition);
                                allAndConditionsTrue = false;
                                break;
                            }

                            const [, variableName, expectedValue] = conditionMatch;

                            // Get the current value of the variable from formValues
                            const currentValue = resolveEnablingCurrent(variableName);

                            // Compare values (case-sensitive as per formula)
                            if (String(currentValue ?? '').trim() !== expectedValue.trim()) {
                                allAndConditionsTrue = false;
                                break;
                            }
                        }

                        // If all AND conditions are true, the OR condition is satisfied
                        if (allAndConditionsTrue) {
                            return true;
                        }
                    }

                    // None of the OR conditions were satisfied
                    return false;
                } catch (error) {
                    console.error('Error evaluating enablingFormula:', error);
                    return false;
                }
            };

            const shouldShow = evaluateFormula(formula);
            if (!shouldShow) {
                return null;
            }
        }
        // If there's an enabling attribute (old logic for backward compatibility), check if it should be shown
        else if (ctrl.enablingAttribute && ctrl.enablingValue) {
            const enablingValue = resolveEnablingCurrent(ctrl.enablingAttribute);

            // Compare case-insensitively and with trimmed values
            const shouldShow = String(enablingValue ?? '').trim().toLowerCase() === String(ctrl.enablingValue).trim().toLowerCase();

            if (!shouldShow) {
                return null;
            }
        }

        // Check if there's any content to display
        const hasDescription = ctrl.description && ctrl.description.trim() !== '';
        const hasPossibleValues = ctrl.possibleValues && ctrl.possibleValues.trim() !== '';
        const hasName = ctrl.name && ctrl.name.trim() !== '';

        // Skip rendering if no content
        if (!hasDescription && !hasPossibleValues && !hasName) {
            return null;
        }

        // Render informational text with URL linkification
        return (
            <div key={controlKey}>
                {hasName && !hasPossibleValues && (
                    <p className="text-sm">
                        <TextWithLinks text={ctrl.name} />
                    </p>
                )}
                {hasPossibleValues && (
                    <p className="text-sm text-gray-700">
                        <TextWithLinks text={ctrl.possibleValues ?? ''} />
                    </p>
                )}
            </div>
        );
    }

    // 1. Checkbox Controls
    if (

        (ctrl.dataType === 'Boolean') // Ensure Test Optional (a dropdown) is excluded
    ) {
        return renderCheckbox(labelText, effectiveIsRequired ?? false);
    }

    // 2. FORCE TEXT/NUMBER INPUT for GPA and Test Score fields
    // Assign true if the control is a GPA or Test Score field
    const gpaTestScoreNames = [
        "GPA",
        "Test Score",
        "SAT Score",
        "ACT Score",
        "GRE Score",
        "GMAT Score"
    ];
    const isGpaOrTestScore = gpaTestScoreNames.includes(ctrl.name);

    if (isGpaOrTestScore) {
        return renderGpaOrTestScore();
    }

    // 3. DYNAMIC STATE FIELD HANDLING - Force State fields to render as dropdowns
    // This ensures State fields in both Student Information and Student Contact render as dropdowns
    // This check must come BEFORE possibleValuesSource check to override autocomplete behavior
    const isStateFieldInContact = (ctrl.name?.toLowerCase() === 'state' ||
        ctrl.name?.toLowerCase() === COMMON_FIELD_NAMES.STATE.toLowerCase()) &&
        (entityName === ENTITY_NAMES.STUDENT_CONTACT ||
            entityName === ENTITY_NAMES.STUDENT_INFORMATION);

    if (isStateFieldInContact) {
        // Force the State field to render as a dropdown
        return renderStringMultivalued();
    }

    // 4. High School Autocomplete with possibleValuesSource
    if (ctrl.possibleValuesSource && ctrl.isMultivalued === false) { // will bind it in backend for possibleValuesSource present and isMultivalued = false for autocomplete
        // Check if homeschooled checkbox is checked
        const studentAttrs = [
            ...allEntityAttributes.studentInfo,
            ...allEntityAttributes.studentContact
        ];
        const homeschooledControl = studentAttrs.find(c =>
            c.name?.toLowerCase().includes(COMMON_FIELD_NAMES.HOMESCHOOL)
        );
        const isHomeschooled = homeschooledControl && formValues[homeschooledControl._id] === BOOLEAN_VALUES.TRUE;

        return (
            <HighSchoolAutocompleteComponent
                key={ctrl._id}
                controlKey={controlKey}
                labelText={labelText}
                ctrl={effectiveCtrl}
                errorMessage={errorMessage ?? ''}
                currentControlValue={currentControlValue}
                dynamicOptions={dynamicOptions ?? null}
                effectiveDynamicOptions={effectiveDynamicOptions ?? null}
                isLoading={isLoadingOptions ?? false}
                onChange={onChange}
                disabled={effectiveDisabled || !!inputDisabled}
                isHomeschooled={isHomeschooled}
            />
        );
    }

    // 5. String Multivalued (Dropdown/Range Select) - Includes Test Optional Question
    if (ctrl.dataType === 'String' && ctrl.isMultivalued) {
        return renderStringMultivalued();
    }

    // 6. Fallback to Simple Text/Number Input
    // Handle Date input separately
    if (ctrl.dataType === 'Date') {
        return (
            <div className="mb-4">
                {renderLabelWithTooltip(ctrl._id, labelText, effectiveIsRequired, ctrl.toolTip)}
                <DatePicker
                    id={ctrl._id}
                    name={ctrl.name || ''}
                    value={currentControlValue ? String(currentControlValue) : null}
                    onChange={(value) => onChange(controlKey, value)}
                    required={effectiveRequiredForValidation ?? false}
                    disabled={effectiveDisabled || !!inputDisabled}
                    error={errorMessage}
                    helperText={ctrl.description ?? undefined}
                    min={ctrl.possibleMinValue ? String(ctrl.possibleMinValue) : undefined}
                    max={ctrl.possibleMaxValue ? String(ctrl.possibleMaxValue) : undefined}
                    placeholder="Select a date"
                />
            </div>
        );
    }

    return renderFallbackInput();
};

export default ControlRenderer;